import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

// ─── Your settings ───────────────────────────────────────────────────────────
// Everything specific to this shop — model and artwork URLs, tuned dial text
// and lighting values, contact details, payment terms — lives in the
// #cb-settings block in clock-builder.liquid, NOT in this file.
//
// That is deliberate. Replacing this file used to wipe those values; now it
// can't, because this file only holds defaults and the liquid overrides them.
// After tuning something live with _face() or _scene(), paste the logged values
// into that block so they survive.
function readSettings() {
  const el = document.getElementById('cb-settings');
  if (!el) {
    console.warn('[clock-builder] No #cb-settings block found — using built-in '
      + 'defaults. Your model URLs and tuned values live there, so check that '
      + 'clock-builder.liquid is up to date.');
    return {};
  }
  try {
    return JSON.parse(el.textContent) || {};
  } catch (e) {
    console.error('[clock-builder] Could not parse #cb-settings — using defaults:', e);
    return {};
  }
}

const SETTINGS = readSettings();

// Merge overrides onto defaults, leaving any key the settings block omits
// alone. Plain objects recurse; everything else replaces.
function applyOverrides(target, source) {
  if (!source || typeof source !== 'object') return target;
  Object.entries(source).forEach(([key, value]) => {
    const isPlain = value && typeof value === 'object' && !Array.isArray(value);
    if (isPlain && target[key] && typeof target[key] === 'object') {
      applyOverrides(target[key], value);
    } else if (value !== null && value !== undefined) {
      target[key] = value;
    }
  });
  return target;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEBUG = SETTINGS.debug === true;
const MODEL_URLS = applyOverrides({
  weather: { glb: 'https://cdn.shopify.com/3d/models/781c3f382b15f401/BB-clock_customiser_v5.glb', artworkGlb: null },
  giant:   { glb: 'https://cdn.shopify.com/3d/models/6731edea70ca9216/Giant_clock_model_v6.glb',  artworkGlb: null }
}, SETTINGS.models);

const GLB_URL = MODEL_URLS.weather.glb;
const GLB_URL_GIANT = MODEL_URLS.giant.glb;

// ─── Artwork options ─────────────────────────────────────────────────────────
// Each illustration has its own geometry, so artwork lives in its own GLB and
// is loaded on demand rather than packed into the clock models. The standard
// artwork is already inside each clock GLB, so its `glb` is null.
//
// An artwork GLB must contain ONLY the art meshes, named exactly as in
// MESH_NAMES (art_background, art_layer_1/2/3), and be exported from the same
// Blender scene position as the clock so it drops straight in — it is added as
// a child of clock_root and inherits its centring and scale.
// Which base layers a custom artwork supersedes is read from the GLB itself:
// whatever art layers the file contains are hidden on the clock, and any layer
// it doesn't contain keeps rendering from the clock GLB. So re-exporting with a
// different set of layers needs no code change, and a layer that sits behind
// the visible scene is left alone simply by leaving it out of the file.
//
// The art layers the builder knows about. Order is back to front.
const ART_LAYERS = ['art_background', 'art_layer_1', 'art_layer_2', 'art_layer_3'];

const ARTWORK = {
  weather: {
    standard: {
      name: 'Our Original Weather Clock artwork',
      desc: 'Originally created by Scottish illustrator, Peter McDermott',
      glb: null
    },
    custom:   {
      name: 'Your Own Fully Custom Artwork',
      desc: 'A one-of-a-kind kinetic Art Clock designed by our artists from your brief',
      glb: MODEL_URLS.weather.artworkGlb,
    },
  },
  giant: {
    standard: {
      name: 'Our Original Weather Clock artwork',
      desc: 'Originally created by Scottish illustrator, Peter McDermott',
      glb: null
    },
    custom:   {
      name: 'Your Own Fully Custom Artwork',
      desc: 'A one-of-a-kind kinetic Art Clock designed by our artists from your brief',
      glb: MODEL_URLS.giant.artworkGlb,
    },
  },
};

// ─── Full custom commission ──────────────────────────────────────────────────
// Selecting custom artwork makes the build a full-custom commission. The
// commission price covers the bespoke clock face and the bespoke artwork ONLY.
// Frame colour/material, the plaque and any other option are charged on top at
// the usual rates.
//
// The commission price lives with every other price, in the #cb-pricing
// block in clock-builder.liquid, so it follows the market currency.

// Payment terms. DEPOSIT_PERCENT is the share taken up front, the balance is
// due on completion. Change this single number to change every figure shown to
// the customer and every line sent to the cart.
const DEPOSIT_PERCENT = typeof SETTINGS.depositPercent === 'number'
  ? SETTINGS.depositPercent
  : 50;

// A fixed deposit instead of a percentage. Set it and the deposit stops moving
// with the frame and plaque choices, which is what makes it sellable through
// the cart: a fixed amount is a fixed-price product, and a percentage of a
// variable total is not.
//
// It is a price, so it lives in the #cb-pricing block, not in settings — a
// round £3,000 should be a round amount in every market, not whatever the
// exchange rate makes of it. Zero or absent means use the percentage.
function depositIsFixed() {
  return PRICES.commissionDeposit > 0;
}

// Customer service contact details and UK business hours, used by the
// commission reassurance panel and the callback request.
const CUSTOMER_SERVICE = applyOverrides({
  email: 'hello@bramwellbrown.com',
  phone: '+44 (0)1962 000000',
  // Local UK time, 24-hour. days: 0 = Sunday ... 6 = Saturday.
  hours: { days: [1, 2, 3, 4, 5], open: 9, close: 17 },
  hoursLabel: 'Monday to Friday, 9am to 5pm UK time'
}, SETTINGS.contact);

// ─── Enquiry submission ──────────────────────────────────────────────────────
// Commission enquiries and saved builds are posted to a single endpoint. Set
// ENQUIRY_ENDPOINT to wire this up to Klaviyo, a form handler, a CRM or an app
// proxy — see submitEnquiry() for the payload contract.
//
// Left null, the builder runs in prototype mode: the form validates, shows its
// success state and logs the payload to the console, so the journey can be
// demonstrated end to end before anything is connected.
const ENQUIRY_ENDPOINT = typeof SETTINGS.enquiryEndpoint === 'string' && SETTINGS.enquiryEndpoint
  ? SETTINGS.enquiryEndpoint
  : null;

// With no endpoint set, the callback form is Shopify's own {% form 'contact' %}
// and submits itself — see the callback modal in clock-builder.liquid. Posting
// to /contact with fetch was tried and returns 400: Shopify injects hidden
// fields that only the form tag supplies, so it cannot be done from script.
// Those submissions go to the sender email in Settings → Notifications, which
// is Shopify's choice of destination, not something this file can set.

// What kind of enquiry each submission is, sent as `type` in the payload.
const ENQUIRY_TYPES = {
  callback: 'commission_callback',
  savedBuild: 'saved_build'
};

// No endpoint, so submitEnquiry() delivers nothing. The callback form does not
// rely on it — it posts itself — but a saved build does, and falls back to the
// customer's own mail client.
function isPrototypeMode() {
  return !ENQUIRY_ENDPOINT;
}

const MESH_NAMES = {
  frame: 'clock_frame',
  face: 'clock_face',
  artBackground: 'art_background',
  artLayer1: 'art_layer_1',
  artLayer2: 'art_layer_2',
  artLayer3: 'art_layer_3',
  swatch1: 'colour_swatch_1',
  swatch2: 'colour_swatch_2',
  swatch3: 'colour_swatch_3',
  plaque: 'clock_plaque',
};

// ─── Per-model presentation settings ─────────────────────────────────────────
// scale     — on-screen size. Weather is 33cm, Giant 53cm, so Giant reads larger.
// plaqueCam — OPTIONAL manual override for the plaque camera. Leave null to let
//             framePlaque() work it out from the plaque's actual bounding box,
//             which adapts automatically to each model's scale.
// faceText  — dial text offsets and sizes, in 1024px canvas space. Two lines
//             only; the clock title was removed at the client's request, so the
//             title* keys are no longer read.
//             Tune live in the console with _face({ subSize: 20, labelY: 128 }).

const MODEL_SETTINGS = applyOverrides({
  weather: {
    scale: 1.2,
    plaqueCam: null,
    plaqueTransform: 'rotate180',
    plaqueOffset: 0,
    faceText: {
      subY:     95, subSize:   26, subTrack: 4,
      labelY:  120, labelSize: 26, labelTrack: 4
    }
  },
  giant: {
    scale: 2.6,
    plaqueCam: null,
    plaqueTransform: 'rotate180',
    plaqueOffset: 0.004,
    faceText: {
      subY:     95, subSize:   26, subTrack: 4,
      labelY:  120, labelSize: 26, labelTrack: 4
    }
  }
}, SETTINGS.models);

function modelSettings() {
  return MODEL_SETTINGS[config.model] || MODEL_SETTINGS.weather;
}

// ─── Studio lighting ─────────────────────────────────────────────────────────
// Tune live with _scene({ exposure: 1.1, ambient: 0.4 }) — see helpers below.
// Intent: a soft studio look. Low ambient so the recessed scenery sits in
// shade, one strong key with shadows for depth, an environment map so metals
// and gloss actually reflect something.
const SCENE_SETTINGS = applyOverrides({
  // Viewer backdrop. A darker backdrop lifts perceived contrast on the dial and
  // lets the frame's edge highlights read. Note it changes what the clock is
  // seen against, not how it is lit — reflections come from scene.environment.
  background: '#f5f4f0',
  // 'neutral' = Khronos PBR Neutral: true blacks, clean whites, no hue shift.
  // 'aces' = filmic, cinematic, but lifts blacks and desaturates — which is
  // what was making the frame read dark grey rather than black.
  toneMapping: 'neutral',
  exposure: 0.85,        // lower = deeper blacks. Raise if the dial dulls.
  ambient: 0.2,         // hemisphere fill. High values flatten everything.
  key: 2.0,              // main light, casts shadows
  // Key light position. A steeper angle throws a longer shadow across the
  // recessed artwork; move it closer to head-on (lower x/y, higher z) to
  // shorten that shadow. Distance from origin also sets the shadow camera range.
  keyPosition: [2.5, 4, 3.5],
  fill: 0.45,            // soft opposite-side fill
  rim: 0.2,              // edge light from behind, separates frame from background
  // Back-of-clock lighting. Every light above sits on the FRONT side, so the
  // rear casing was lit by the 0.2 rim and ambient alone — near black, with the
  // engraved pattern only readable when a reflection happened to catch it.
  // This light follows the camera and fades in as the view comes round the
  // back, so the tuned front lighting is untouched.
  backLight: 3.6,        // peak intensity, reached when looking straight at the back
  // Degrees the light is offset from the camera. Dead-on (0,0) gives a central
  // hotspot and flattens everything — the detail reads because the light RAKES
  // across it. Up and to the left matches how the key lights the front.
  backLightYaw: 40,
  backLightPitch: 5,
  envIntensity: 1,    // reflection strength on materials
  artDarken: 0.5,       // multiply artwork colours — it sits behind glass, in shade
  artEnv: 0.10,          // environment light on the artwork. THIS is what was
                         // flooding the recess: the env map lights it evenly
                         // from all directions, cancelling any sense of shade.
  artRoughness: 0.95,    // matt paper, not semi-gloss — kills stray highlights
  glassOpacity: 0.09,    // 0 = invisible, higher = tint. Keep low.
  shadows: true,         // master switch — set false to rule shadows out
  // Meshes matching this do NOT cast shadows, but still receive them.
  // The art layers are thin planes sitting almost coincident with each other;
  // when such a plane casts, its own depth lands in the shadow map at roughly
  // its own surface depth and it reads as self-shadowed — the stair-stepping
  // seen on the FAIR indicator (art_layer_3). Everything else still casts, so
  // the dial and front casing continue to shade the recessed scenery.
  shadowNoCast: /^art_|artwork/i,
  shadowSoftness: 4,   // shadow blur radius
  shadowMapSize: 4096,   // higher = finer shadow detail, less stair-stepping
  // × model scale. At scale 1.2 one world unit is ~27.5cm, so 0.004 ≈ 1.1mm.
  // The clock_face to art_layer_3 gap is ~6mm, and bias must stay a small
  // fraction of it: too low gives acne, too high and the sample steps past
  // the layer entirely and the shadow disappears.
  shadowNormalBias: 0.0035,
  shadowBias: -0.00008,
  // Zoom limits are derived from each model's scale so the small Weather clock
  // can be inspected as closely as the Giant. Multipliers of model scale.
  minZoom: 0.70,
  maxZoom: 2.60,
}, SETTINGS.scene);

// ─── Metal finish adjustment ─────────────────────────────────────────────────
// These materials come from the GLB with a brushed texture. material.color
// MULTIPLIES that texture rather than replacing it, so a tint shifts the hue
// while keeping the brushing intact. '#ffffff' = unchanged.
// Tune live: _finish('copper', { tint: '#e8913f', roughness: 0.32 })
// NOTE: the key 'aluminium' is the internal name — it maps to colour_swatch_3
// in the GLB. The finish is shown to customers as "Chrome". Renaming the key
// would mean touching the mesh mapping too, so only the label was changed.
const FINISH_SETTINGS = applyOverrides({
  copper: {
    tint: '#ffffff',
    metalness: 1,      // null = leave the GLB's own value alone
    roughness: 0.22,
  },
  aluminium: {
    tint: '#ffffff',
    metalness: 1,
    roughness: 0.22,
  },
}, SETTINGS.finishes);

const TONE_MAPPING = {
  neutral: THREE.NeutralToneMapping,
  aces: THREE.ACESFilmicToneMapping,
  linear: THREE.LinearToneMapping,
  reinhard: THREE.ReinhardToneMapping,
};

// ─── State ───────────────────────────────────────────────────────────────────

const meshes = {};
const materials = {};
const lights = {};
let scene, camera, renderer, controls;
// Incremented on every load. A load whose token no longer matches has been
// superseded by a newer one and must discard its result.
let loadToken = 0;

// ─── Init ────────────────────────────────────────────────────────────────────

function init() {
  const container = document.getElementById('cb-viewer');
  if (!container) return;

  const w = container.clientWidth;
  const h = container.clientHeight;

  scene = new THREE.Scene();
  scene.background = new THREE.Color(SCENE_SETTINGS.background);

  const isMobile = window.innerWidth <= 900;
  camera = new THREE.PerspectiveCamera(isMobile ? 40 : 45, w / h, 0.1, 100);
  camera.position.set(0, 0, 3);

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(w, h);
  renderer.toneMapping = TONE_MAPPING[SCENE_SETTINGS.toneMapping] || THREE.NeutralToneMapping;
  renderer.toneMappingExposure = SCENE_SETTINGS.exposure;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  container.innerHTML = '';
  container.appendChild(renderer.domElement);

  // Shadows: needed so the recessed scenery reads as shaded, not floodlit.
  renderer.shadowMap.enabled = SCENE_SETTINGS.shadows;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  // Environment: gives metals and gloss something to reflect. RoomEnvironment
  // is a procedural studio, so no HDR file has to be hosted.
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();

  // Lighting rig
  lights.ambient = new THREE.HemisphereLight(0xffffff, 0xd8d4cc, SCENE_SETTINGS.ambient);
  scene.add(lights.ambient);

  lights.key = new THREE.DirectionalLight(0xffffff, SCENE_SETTINGS.key);
  lights.key.position.set(...SCENE_SETTINGS.keyPosition);
  lights.key.castShadow = SCENE_SETTINGS.shadows;
  lights.key.shadow.mapSize.set(SCENE_SETTINGS.shadowMapSize, SCENE_SETTINGS.shadowMapSize);
  // near/far and bias are set by fitShadowCamera() once the model is loaded,
  // since both depend on the model's scale.
  lights.key.shadow.radius = SCENE_SETTINGS.shadowSoftness;
  scene.add(lights.key);

  lights.fill = new THREE.DirectionalLight(0xffffff, SCENE_SETTINGS.fill);
  lights.fill.position.set(-3, 1, 2);
  scene.add(lights.fill);

  lights.rim = new THREE.DirectionalLight(0xfff4e8, SCENE_SETTINGS.rim);
  lights.rim.position.set(0, 2, -3);
  scene.add(lights.rim);

  // Starts dark; updateBackLight() drives it from the camera each frame.
  lights.back = new THREE.DirectionalLight(0xffffff, 0);
  scene.add(lights.back);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.enablePan = true;
  controls.autoRotate = false;

  // OrbitControls sets touchAction = 'none' on the canvas in its constructor,
  // which takes EVERY touch. On a phone, if the canvas ends up filling the
  // screen — zoomed in, or just scrolled to the top — there is then no gesture
  // left that scrolls the page, and the viewer traps the visitor. A reload
  // doesn't help either, because the browser restores the zoom and scroll.
  //
  // 'pan-y' hands vertical drags back to the browser, so dragging up or down
  // always scrolls the page and there is always a way out. Horizontal drags
  // still reach the controls and rotate the clock, which is the rotation that
  // matters here, and a two-finger pinch still dollies.
  //
  // Set after the constructor on purpose: it writes an inline style, so CSS
  // cannot override it.
  controls.domElement.style.touchAction = 'pan-y';

  loadModel();
  window.addEventListener('resize', onResize);

  document.querySelectorAll('.cb-view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.cb-view-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      setView(btn.dataset.view);
    });
  });

  const zoomInBtn = document.getElementById('cb-zoom-in');
  const zoomOutBtn = document.getElementById('cb-zoom-out');
  if (zoomInBtn) zoomInBtn.addEventListener('click', () => zoomBy(0.8));
  if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => zoomBy(1.25));

  animate();
}

// ─── Loader factory ───────────────────────────────────────────────────────────

function createLoader() {
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm/libs/draco/');
  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);
  loader.crossOrigin = 'anonymous';
  return loader;
}

// ─── Model loading ───────────────────────────────────────────────────────────

function logGLBContents(label, model) {
  if (!DEBUG) return;
  console.group(label);
  model.traverse((obj) => {
    if (obj.isMesh) {
      console.log('Mesh:', obj.name, '| Material:', Array.isArray(obj.material)
        ? obj.material.map(m => m.name)
        : obj.material.name
      );
    }
  });
  console.groupEnd();
}

// Map meshes to their logical names. An exact name wins; a suffixed primitive
// is only used when nothing exact turned up, so a multi-material object still
// resolves instead of being silently skipped. This is why the mesh count used
// to come back lower than the model really contained.
function mapMeshes(model) {
  const fallback = {};
  model.traverse((obj) => {
    if (!obj.isMesh) return;
    Object.entries(MESH_NAMES).forEach(([key, name]) => {
      if (obj.name === name) meshes[key] = obj;
      else if (!fallback[key] && isLayerMesh(obj.name, name)) fallback[key] = obj;
    });
  });
  Object.entries(fallback).forEach(([key, obj]) => {
    if (!meshes[key]) meshes[key] = obj;
  });
}

// Applied to every mesh after load: shadows on, reflections on, artwork sat
// back into shade, glass made properly transparent.
function applyPresentation(model) {
  const artMaterial = /artwork|paper|bb_background/i;
  // Only the outer frame casts shadows. Everything else sits within a few
  // millimetres of the dial, and at that separation a shadow map cannot
  // resolve the gap — the surface ends up shadowing itself, which shows as
  // stair-stepped artefacts across the face. They all still RECEIVE shadow,
  // so the recess and the frame's depth still read correctly.
  const noCast = SCENE_SETTINGS.shadowNoCast;
  model.traverse((obj) => {
    if (!obj.isMesh) return;
    obj.castShadow = !noCast.test(obj.name);
    obj.receiveShadow = true;

    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((m) => {
      if (!m) return;
      if ('envMapIntensity' in m) m.envMapIntensity = SCENE_SETTINGS.envIntensity;

      // Glass: make it read as glass rather than a grey film over the face
      if (/glass/i.test(obj.name) || /glass/i.test(m.name || '')) {
        m.transparent = true;
        m.opacity = SCENE_SETTINGS.glassOpacity;
        m.roughness = 0.05;
        m.metalness = 0;
        m.depthWrite = false;
        obj.castShadow = false;
        obj.renderOrder = 10;
      }

      // Scenery: recessed behind the dial and the glass, so it should read as
      // shaded. Three things matter here, and the env map is the big one —
      // it lights the artwork evenly from every direction, which cancels out
      // the shading the recess would otherwise give.
      if (artMaterial.test(m.name || '') && m.color) {
        m.userData._baseColor = m.userData._baseColor || m.color.clone();
        m.color.copy(m.userData._baseColor).multiplyScalar(SCENE_SETTINGS.artDarken);
        if ('envMapIntensity' in m) m.envMapIntensity = SCENE_SETTINGS.artEnv;
        if ('roughness' in m) m.roughness = SCENE_SETTINGS.artRoughness;
        if ('metalness' in m) m.metalness = 0;
        m.userData._isArt = true;
      }
      m.needsUpdate = true;
    });
  });
}

function fitModel(model) {
  const box = new THREE.Box3().setFromObject(model);
  const centre = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  model.position.sub(centre);
  model.scale.setScalar(modelSettings().scale / maxDim);
  applyZoomLimits();
  fitShadowCamera();
}

// The shadow map covers a fixed area. If that area is much larger than the
// model, each texel covers more surface and edges stair-step. Size it to the
// model so the available resolution is spent where it matters.
function fitShadowCamera() {
  const key = lights.key;
  if (!key || !key.shadow) return;

  const scale = modelSettings().scale;
  const r = scale * 0.8;
  const cam = key.shadow.camera;

  // Width/height: spend the map's texels on the model, not empty space.
  cam.left = -r; cam.right = r; cam.top = r; cam.bottom = -r;

  // Depth: this is the one that matters here. The shadow map's depth precision
  // is spread across near..far. A default range of 0.5–20 for a model ~1.2
  // units across leaves almost no precision for the millimetre gap between
  // clock_face and art_layer_3 behind it — so the face's shadow bleeds onto
  // the layer as stair-stepped acne. Clamp the range tightly around the model.
  const distance = key.position.length();
  cam.near = Math.max(0.05, distance - r * 1.6);
  cam.far = distance + r * 1.6;
  cam.updateProjectionMatrix();

  // Bias is in world units, so it has to scale with the model or it means
  // something different on each. At scale 1.2 one world unit is ~27cm.
  key.shadow.normalBias = SCENE_SETTINGS.shadowNormalBias * scale;
  key.shadow.bias = SCENE_SETTINGS.shadowBias;
  key.shadow.needsUpdate = true;
}

// The two models render at different sizes, so fixed zoom limits let you get
// close to the Giant but not the Weather clock. Derive them from model scale
// instead, so both can be inspected at the same relative closeness.
function applyZoomLimits() {
  if (!controls) return;
  const scale = modelSettings().scale;
  const isMobile = window.innerWidth <= 900;
  // Only the close limit scales — the Weather clock renders smaller, so it
  // needs to allow a closer approach to inspect the frame finish.
  controls.minDistance = scale * SCENE_SETTINGS.minZoom * (isMobile ? 0.85 : 1);
  // Keep the far limit clear of the default framing distance (z = 3).
  controls.maxDistance = Math.max(scale * SCENE_SETTINGS.maxZoom, 4);
  controls.update();
}

function loadModel() {
  const loader = createLoader();
  const url = config.model === 'giant' ? GLB_URL_GIANT : GLB_URL;
  const token = ++loadToken;

  loader.load(
    url,
    (gltf) => {
      if (token !== loadToken) return;

      removeClockRoots();
      const model = gltf.scene;
      model.name = 'clock_root';

      logGLBContents('GLB contents', model);
      mapMeshes(model);
      applyPresentation(model);
      fitModel(model);
      scene.add(model);

      const loading = document.getElementById('cb-loading');
      if (loading) loading.remove();

      collectBaseArt(model);
      extractMaterials();
      initFaceCanvas();
      initPlaqueCanvas();
      setArtwork(config.artwork);
      setView('front');

      if (DEBUG) console.log('Meshes found:', Object.keys(meshes));
    },
    (progress) => {
      if (progress.total > 0) {
        const pct = Math.round((progress.loaded / progress.total) * 100);
        const loading = document.getElementById('cb-loading');
        if (loading) loading.querySelector('span').textContent = `Loading... ${pct}%`;
      }
    },
    (error) => {
      console.error('GLB load error:', error);
      const loading = document.getElementById('cb-loading');
      if (loading) loading.querySelector('span').textContent = 'Failed to load model.';
    }
  );
}

// Remove every clock root in the scene. getObjectByName returns only the
// first match, so switching models faster than the GLB loads could leave a
// second model stranded in the scene.
function removeClockRoots() {
  scene.children
    .filter(o => o.name === 'clock_root')
    .forEach(o => {
      scene.remove(o);
      // Custom artwork groups are cached and re-added on the next selection,
      // so detach them before disposal or the cache would hold dead geometry.
      o.children
        .filter(c => /^artwork_/.test(c.name))
        .forEach(c => o.remove(c));
      o.traverse((child) => {
        if (!child.isMesh) return;
        child.geometry?.dispose();
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(m => m?.dispose());
      });
    });
}

function swapModel(modelKey) {
  const url = modelKey === 'giant' ? GLB_URL_GIANT : GLB_URL;
  if (!url) return;

  const token = ++loadToken;
  removeClockRoots();
  activeArtworkGroup = null;
  baseArtMeshes = [];

  Object.keys(meshes).forEach(k => delete meshes[k]);
  faceCanvas = null;
  faceTexture = null;
  faceBaseImage = null;
  plaqueCanvas = null;
  plaqueTexture = null;
  plaqueBaseZ = null;

  const viewer = document.getElementById('cb-viewer');
  const placeholder = document.createElement('div');
  placeholder.className = 'cb-preview__placeholder';
  placeholder.id = 'cb-loading';
  placeholder.innerHTML = '<span>Loading model...</span>';
  if (viewer) viewer.appendChild(placeholder);

  const loader = createLoader();
  loader.load(
    url,
    (gltf) => {
      // A newer swap started while this was loading — discard this result.
      if (token !== loadToken) return;

      removeClockRoots();
      const model = gltf.scene;
      model.name = 'clock_root';

      logGLBContents('GLB contents (' + modelKey + ')', model);
      mapMeshes(model);
      applyPresentation(model);
      fitModel(model);
      scene.add(model);

      const loading = document.getElementById('cb-loading');
      if (loading) loading.remove();

      collectBaseArt(model);
      extractMaterials();
      initFaceCanvas();
      initPlaqueCanvas();
      setArtwork(config.artwork);
      setView('front');

      if (DEBUG) console.log('Swapped to model:', modelKey, 'Meshes:', Object.keys(meshes));
    },
    null,
    (error) => {
      console.error('Model swap error:', error);
      const loading = document.getElementById('cb-loading');
      if (loading) loading.querySelector('span').textContent = 'Failed to load model.';
    }
  );
}

// ─── Artwork ─────────────────────────────────────────────────────────────────

// Art meshes that came with the clock GLB (the standard illustration).
let baseArtMeshes = [];
// Loaded custom artwork groups, keyed 'model:artworkId' so switching back and
// forth doesn't re-download.
const artworkCache = new Map();
let activeArtworkGroup = null;

function collectBaseArt(model) {
  baseArtMeshes = [];
  model.traverse((o) => {
    if (o.isMesh && /^art_/i.test(o.name)) baseArtMeshes.push(o);
  });
}

// A Blender object with more than one material exports as one glTF primitive
// per material, and each primitive arrives as its own mesh with a numeric
// suffix. So the single Blender object `art_layer_2` carrying "Paper Edges" and
// "artwork-2" shows up as art_layer_2_1 and art_layer_2_2, while a
// single-material object keeps its bare name.
//
// Never match a layer by exact mesh name for that reason — match the layer the
// mesh belongs to.
function isLayerMesh(meshName, layerName) {
  if (meshName === layerName) return true;
  if (!meshName.startsWith(layerName + '_')) return false;
  // Only a numeric suffix counts, so art_layer_1 doesn't swallow art_layer_11.
  return /^\d+$/.test(meshName.slice(layerName.length + 1));
}

function meshBelongsToAnyLayer(meshName, layerNames) {
  return layerNames.some(layer => isLayerMesh(meshName, layer));
}

// Show or hide base art meshes. With no list, every one of them; with a list,
// only the meshes belonging to those layers, so untouched layers keep
// rendering from the clock GLB.
function setBaseArtVisible(visible, names) {
  baseArtMeshes.forEach(o => {
    if (!names || meshBelongsToAnyLayer(o.name, names)) o.visible = visible;
  });
}

// Point meshes.art* at whichever set is currently showing, so anything that
// looks them up gets the active artwork.
function mapArtMeshes(root) {
  const fallback = {};
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    Object.entries(MESH_NAMES).forEach(([key, name]) => {
      if (!/^art/i.test(key)) return;
      if (obj.name === name) meshes[key] = obj;
      else if (!fallback[key] && isLayerMesh(obj.name, name)) fallback[key] = obj;
    });
  });
  Object.entries(fallback).forEach(([key, obj]) => {
    if (!meshes[key]) meshes[key] = obj;
  });
}

function setArtwork(artworkId) {
  config.artwork = artworkId;

  const clockRoot = scene.getObjectByName('clock_root');
  if (!clockRoot) return;

  // Detach whatever custom artwork is showing
  if (activeArtworkGroup) {
    clockRoot.remove(activeArtworkGroup);
    activeArtworkGroup = null;
  }

  const option = (ARTWORK[config.model] || {})[artworkId];

  // Standard artwork lives in the clock GLB itself
  if (!option || !option.glb) {
    setBaseArtVisible(true);
    mapArtMeshes(clockRoot);
    console.log('[clock-builder] Standard artwork restored for', config.model);
    return;
  }

  // Not configured: fall back to the standard artwork rather than an empty
  // recess. Set it in the #cb-settings block in clock-builder.liquid.
  if (typeof option.glb !== 'string' || !option.glb) {
    console.warn('[clock-builder] No artwork GLB configured for', config.model, artworkId,
      '\u2014 set models.' + config.model + '.artworkGlb in the #cb-settings block.');
    setBaseArtVisible(true);
    return;
  }

  const cacheKey = config.model + ':' + artworkId;
  const cached = artworkCache.get(cacheKey);
  if (cached) {
    attachArtwork(cached, clockRoot, artworkId);
    return;
  }

  const token = ++loadToken;
  createLoader().load(
    option.glb,
    (gltf) => {
      if (token !== loadToken) return;
      const group = gltf.scene;
      group.name = 'artwork_' + artworkId;
      applyPresentation(group);
      artworkCache.set(cacheKey, group);

      // Only attach if this artwork is still the selected one
      const root = scene.getObjectByName('clock_root');
      if (root && config.artwork === artworkId) attachArtwork(group, root, artworkId);
    },
    null,
    (error) => {
      console.error('Artwork load error:', error);
      setBaseArtVisible(true);
    }
  );
}

// Which art layers a loaded artwork group supplies. This is what decides the
// swap: the file's contents are the declaration, so there is no list in the
// code to keep in sync with the asset.
function layersSuppliedBy(group) {
  const meshNames = [];
  group.traverse(o => { if (o.isMesh) meshNames.push(o.name); });

  const supplied = ART_LAYERS.filter(
    layer => meshNames.some(meshName => isLayerMesh(meshName, layer))
  );

  // An art mesh that matches no known layer is a naming mistake: it will render
  // on top of a base layer that was never hidden, and the two will z-fight.
  const unrecognised = meshNames.filter(
    n => /^art/i.test(n) && !ART_LAYERS.some(layer => isLayerMesh(n, layer))
  );
  if (unrecognised.length) {
    console.warn('[clock-builder] Artwork contains art meshes that match no known layer: '
      + unrecognised.join(', ') + '. Expected one of ' + ART_LAYERS.join(', ')
      + ' (a numeric suffix per material is fine). These will not replace anything.');
  }
  return supplied;
}

// Hide the base layers this artwork supplies, then add it. Hiding happens here
// rather than before the load so the standard artwork stays on screen while a
// custom one downloads, instead of showing an empty recess.
function attachArtwork(group, root, artworkId) {
  const supplied = layersSuppliedBy(group);
  if (!supplied.length) {
    console.warn('[clock-builder] Artwork "' + artworkId + '" supplies no recognised art '
      + 'layers, so nothing was swapped.');
  }
  setBaseArtVisible(true);
  setBaseArtVisible(false, supplied);
  root.add(group);
  activeArtworkGroup = group;
  mapArtMeshes(group);
  console.log('[clock-builder] Artwork "' + artworkId + '" attached; layers swapped:',
    supplied.join(', ') || '(none)');
}


// Rebuild the artwork card list for the current model.
function renderArtworkOptions() {
  const grid = document.getElementById('cb-artwork-grid');
  if (!grid) {
    console.warn('[clock-builder] #cb-artwork-grid is missing from the page — '
      + 'the artwork selector and the commission panel cannot appear. '
      + 'Check that step 2 of clock-builder.liquid is up to date.');
    return;
  }
  const options = ARTWORK[config.model] || {};

  grid.innerHTML = Object.entries(options).map(([id, o]) => `
    <div class="cb-artwork-card${id === config.artwork ? ' active' : ''}" data-artwork="${id}">
      <div class="cb-artwork-card__name">${o.name}</div>
      <div class="cb-artwork-card__desc">${o.desc}</div>
      <div class="cb-artwork-card__price">${id === 'custom'
        ? 'From ' + formatMoney(PRICES.fullCustom[config.model])
        : 'Included'}</div>
    </div>
  `).join('');

  grid.querySelectorAll('.cb-artwork-card').forEach(card => {
    card.addEventListener('click', () => {
      grid.querySelectorAll('.cb-artwork-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');
      setArtwork(card.dataset.artwork);
      updatePrice();
      updateFullCustomNotice();
      updateFooterNav();

    });
  });
}

// Show or hide everything that only applies to a full custom commission, and
// refresh the figures inside it.
function updateFullCustomNotice() {
  const full = isFullCustom();

  document.querySelectorAll('.cb-fullcustom-notice').forEach(el => {
    el.style.display = full ? 'block' : 'none';
  });

  // Blocks marked data-fullcustom clear to '' rather than 'block', so a flex
  // container keeps its own display value from the stylesheet.
  document.querySelectorAll('[data-fullcustom]').forEach(el => {
    el.style.display = full ? '' : 'none';
  });

  // The face-text toggle reads "Included" on a commission and carries the
  // charge otherwise, so it has to follow the artwork choice.
  updateFaceTextPriceLabel();

  if (full) updateCommissionFigures();
}

// Fill the commission panel's prices, payment terms and opening hours. Called
// whenever the total could have moved, so the deposit figure never goes stale.
function updateCommissionFigures() {
  // Written by class, not id: the same figure appears in the artwork-step brief,
  // the review panel and the how-it-works modal, and all three must agree.
  const set = (cls, text) => {
    document.querySelectorAll('.' + cls).forEach(el => { el.textContent = text; });
  };

  set('cb-fig-price', formatMoney(PRICES.fullCustom[config.model]));
  set('cb-fig-total', formatMoney(calcTotal()));
  set('cb-fig-deposit', formatMoney(depositAmount()));
  set('cb-fig-balance', formatMoney(balanceAmount()));
  // With a fixed deposit there is no meaningful percentage to quote, and the
  // remainder isn't a share either — so these spans carry amounts instead, and
  // sentences like "a 50% deposit" become "a £3,000 deposit" without the copy
  // needing to change.
  set('cb-fig-deposit-pct', depositIsFixed()
    ? formatMoney(depositAmount())
    : DEPOSIT_PERCENT + '%');
  set('cb-fig-balance-pct', depositIsFixed()
    ? formatMoney(balanceAmount())
    : (100 - DEPOSIT_PERCENT) + '%');
  // The phone number was removed from the copy. These two lines stay as the
  // wiring for it: add a .cb-fig-phone span or a .cb-commission-phone-link
  // anchor back into the markup and they fill themselves in again.
  set('cb-fig-phone', CUSTOMER_SERVICE.phone);
  const tel = 'tel:' + CUSTOMER_SERVICE.phone.replace(/[^\d+]/g, '');
  document.querySelectorAll('.cb-commission-phone-link').forEach(el => { el.href = tel; });

  // The chat button only promises an immediate answer when someone is there.
  const open = isBusinessHours();
  document.querySelectorAll('.cb-commission-chat').forEach(btn => {
    btn.textContent = open ? 'Chat with us now' : 'Leave us a message';
  });
  document.querySelectorAll('.cb-commission__chat-state').forEach(el => {
    el.textContent = open
      ? 'We\u2019re online now \u2014 ' + CUSTOMER_SERVICE.hoursLabel
      : 'Currently closed \u2014 ' + CUSTOMER_SERVICE.hoursLabel;
    el.classList.toggle('is-open', open);
  });
}

// ─── Live chat ───────────────────────────────────────────────────────────────

// Open whichever chat widget the theme loads. Each supported provider exposes
// its own global, so try them in turn and fall back to the callback form if
// none is present (or the widget has not finished loading).
// Search through nested shadow roots. Shopify's chat is a custom element whose
// activator sits two shadow roots deep, so an ordinary querySelector can't
// reach it — open shadow roots are scriptable, just not CSS-addressable.
function deepQuery(root, selector, depth) {
  depth = depth || 0;
  if (!root || depth > 5) return null;
  try {
    const direct = root.querySelector(selector);
    if (direct) return direct;
    const all = root.querySelectorAll('*');
    for (const el of all) {
      if (el.shadowRoot) {
        const hit = deepQuery(el.shadowRoot, selector, depth + 1);
        if (hit) return hit;
      }
    }
  } catch (e) { /* a closed root throws; keep looking elsewhere */ }
  return null;
}

// Is the chat panel showing? The activator is a toggle, so clicking it while
// the chat is already open closes it — which looked like a dead button.
function shopifyChatIsOpen(host) {
  if (!host) return false;
  if (host.hasAttribute('open')) return true;
  const panel = deepQuery(host.shadowRoot, '[part~="dialog"], section.panel');
  if (!panel) return false;
  return !!(panel.offsetWidth || panel.offsetHeight || panel.getClientRects().length);
}

function findChatActivator(host) {
  return deepQuery(host.shadowRoot,
    '[part~="activator"], button.activator, .activator button, .activator, '
    + 'button[aria-label*="hat"], button[aria-label*="essage"]');
}

// Shopify's own chat. There is no documented API for it, so this clicks the
// activator the visitor would click. `exportparts="activator, dialog"` on the
// inner runtime element is what makes both findable by part name.
//
// Async because the panel animates in: clicking and checking in the same tick
// always reports failure, and the caller needs a truthful answer to decide
// whether to fall back to the callback form.
async function openShopifyChat() {
  const host = document.querySelector('shopify-chat');
  if (!host) return false;

  // Already open — clicking again would close it.
  if (shopifyChatIsOpen(host)) return true;

  const activator = findChatActivator(host);
  if (activator) {
    const target = activator.tagName === 'BUTTON'
      ? activator
      : (activator.querySelector && activator.querySelector('button')) || activator;
    target.click();
    if (await waitForChat(host)) return true;
  }

  // Some builds reflect open state as an attribute on the host.
  try {
    host.setAttribute('open', '');
    if (await waitForChat(host)) return true;
  } catch (e) { /* fall through */ }

  console.warn('[clock-builder] Found <shopify-chat> but could not open it. '
    + 'Run _chat() to see what is inside it.');
  return false;
}

// Poll briefly rather than guessing a fixed delay, so a slow widget still
// reports honestly and a fast one doesn't wait.
function waitForChat(host) {
  return new Promise(resolve => {
    const deadline = Date.now() + 800;
    const tick = () => {
      if (shopifyChatIsOpen(host)) return resolve(true);
      if (Date.now() > deadline) return resolve(false);
      setTimeout(tick, 60);
    };
    tick();
  });
}

async function openLiveChat() {
  try {
    if (await openShopifyChat()) return true;

    // Other providers, in case the client ever switches.
    if (window.ShopifyChat && typeof window.ShopifyChat.open === 'function') {
      window.ShopifyChat.open(); return true;
    }
    if (window.Intercom) { window.Intercom('show'); return true; }
    if (window.tidioChatApi) { window.tidioChatApi.open(); return true; }
    if (window.$crisp) { window.$crisp.push(['do', 'chat:open']); return true; }
    if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
      window.GorgiasChat.open(); return true;
    }
    if (window.zE) { window.zE('messenger', 'open'); return true; }
  } catch (e) {
    console.warn('Live chat failed to open:', e);
  }
  return false;
}

// ─── Callback request ────────────────────────────────────────────────────────

// Send the build link to customer service with a request to call back. The
// submission itself goes through submitEnquiry(), which is the one place to
// connect a real service.
function openCallbackModal() {
  const modal = document.getElementById('cb-callback-modal');
  if (!modal) return;
  const linkEl = document.getElementById('cb-callback-link');
  if (linkEl) linkEl.value = getSaveURL();
  const err = document.getElementById('cb-callback-error');
  if (err) err.style.display = 'none';
  // After a native post Shopify renders the success notice INSTEAD of the form,
  // so there is nothing to restore and nothing to clear — leaving the notice up
  // is the correct state until the page is loaded afresh.
  const form = document.getElementById('cb-callback-form');
  if (form) form.style.display = '';
  modal.style.display = 'flex';
}

function closeCallbackModal() {
  const modal = document.getElementById('cb-callback-modal');
  if (modal) modal.style.display = 'none';
}

// ─── Enquiry transport ───────────────────────────────────────────────────────

// Phone numbers are normalised to E.164 before they leave the page, because
// most CRMs and email platforms reject anything else. Returns null when the
// number can't be normalised confidently, and the form treats that as invalid.
function toE164(raw) {
  const digits = String(raw || '').replace(/[^\d+]/g, '');
  if (/^\+[1-9]\d{7,14}$/.test(digits)) return digits;
  if (/^07\d{9}$/.test(digits)) return '+44' + digits.slice(1);       // 07700 900000
  if (/^0[1-9]\d{8,9}$/.test(digits)) return '+44' + digits.slice(1); // 01962 xxxxxx
  if (/^447\d{9}$/.test(digits)) return '+' + digits;
  return null;
}

// The single integration point. Everything the form collects, plus the full
// build specification, arrives here as one object:
//
//   {
//     type:        'commission_callback' | 'saved_build',
//     submittedAt: ISO 8601 string,
//     contact:     { name, firstName, lastName, email, phone, phoneE164 },
//     consent:     { marketing: boolean },
//     enquiry:     { bestTimeToCall, notes },        // callback only
//     build:       { link, model, artwork, fullCustom, total, deposit,
//                    balance, depositPercent, spec: { ...line items } },
//     context:     { withinBusinessHours, page }
//   }
//
// Resolves true when accepted, false on any failure. To connect this, set
// ENQUIRY_ENDPOINT and adapt the fetch below to whatever shape the receiving
// service wants — it is deliberately a plain JSON POST and nothing else.
async function submitEnquiry(payload) {
  if (isPrototypeMode()) {
    // Prototype mode: nothing is sent anywhere. Log the payload so the shape
    // is visible to whoever connects this, and report success so the form's
    // confirmation state can be demonstrated.
    console.log('[clock-builder] Enquiry (prototype mode — not sent):', payload);
    return true;
  }

  try {
    const res = await fetch(ENQUIRY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      console.error('Enquiry submission failed:', res.status, await res.text().catch(() => ''));
      return false;
    }
    return true;
  } catch (e) {
    console.error('Enquiry submission failed:', e);
    return false;
  }
}

// The build, flattened for the payload. `spec` is the same set of key/value
// pairs that goes onto the cart line item, so an order and an enquiry describe
// the clock identically.
function buildEnquiryBuild() {
  return {
    link: getSaveURL(),
    model: config.model,
    artwork: config.artwork,
    fullCustom: isFullCustom(),
    total: calcTotal(),
    deposit: isFullCustom() ? depositAmount() : null,
    balance: isFullCustom() ? balanceAmount() : null,
    depositPercent: depositIsFixed() ? null : DEPOSIT_PERCENT,
    depositFixed: depositIsFixed() ? depositAmount() : null,
    spec: buildLineItemProperties()
  };
}

function splitName(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : ''
  };
}

// The callback form is now Shopify's own <form>, submitted natively, so this
// runs on submit and either lets the browser through or stops it. The async
// endpoint path is still here and takes over the moment enquiryEndpoint is set.
async function sendCallbackRequest(submitEvent) {
  const val = (id) => ((document.getElementById(id) || {}).value || '').trim();
  const fields = {
    name: val('cb-callback-name'),
    email: val('cb-callback-email'),
    phone: val('cb-callback-phone'),
    when: val('cb-callback-when'),
    note: val('cb-callback-note')
  };
  const consentEl = document.getElementById('cb-callback-consent');
  const consent = consentEl ? consentEl.checked : false;

  const err = document.getElementById('cb-callback-error');
  const sent = document.getElementById('cb-callback-sent');
  const btn = document.getElementById('cb-callback-send');
  const fail = (msg) => {
    if (err) { err.textContent = msg; err.style.display = 'block'; }
  };

  if (err) err.style.display = 'none';
  if (sent) sent.style.display = 'none';

  // A native submit has to be stopped before the browser leaves the page, so
  // every failure path below cancels it first.
  const stop = () => { if (submitEvent) submitEvent.preventDefault(); };
  const reject = (msg) => { stop(); return fail(msg); };

  if (!fields.name) return reject('Please tell us your name.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(fields.email)) {
    return reject('Please enter an email address we can confirm the details to.');
  }
  if (!fields.phone) return reject('Please give us a number to call.');
  const phoneE164 = toE164(fields.phone);
  if (!phoneE164) {
    return reject('That number doesn’t look right. Please check it, or include the country code.');
  }

  const names = splitName(fields.name);
  const payload = {
    type: ENQUIRY_TYPES.callback,
    submittedAt: new Date().toISOString(),
    contact: {
      name: fields.name,
      firstName: names.firstName,
      lastName: names.lastName,
      email: fields.email,
      phone: fields.phone,
      phoneE164: phoneE164
    },
    consent: { marketing: consent },
    enquiry: {
      bestTimeToCall: fields.when || 'Any time during business hours',
      notes: fields.note
    },
    build: buildEnquiryBuild(),
    context: {
      withinBusinessHours: isBusinessHours(),
      page: window.location.pathname
    }
  };

  // No endpoint: let Shopify's form submit itself. Fill the hidden fields so
  // the specification travels with it, and park the build so the reload can
  // put the customer back where they were.
  if (!ENQUIRY_ENDPOINT) {
    const spec = document.getElementById('cb-callback-spec');
    const hours = document.getElementById('cb-callback-hours');
    const link = document.getElementById('cb-callback-link');
    if (link && !link.value) link.value = getSaveURL();
    if (hours) hours.value = isBusinessHours() ? 'yes' : 'no';
    if (spec) spec.value = specAsText(payload.build);
    stashBuildForReload();
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    return true;   // the browser posts the form from here
  }

  stop();
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  const ok = await submitEnquiry(payload);
  if (btn) { btn.disabled = false; btn.textContent = 'Send my callback request'; }

  if (ok) {
    const form = document.getElementById('cb-callback-form');
    if (form) form.style.display = 'none';
    if (sent) sent.style.display = 'block';
  } else {
    fail('We couldn’t send that just now. Please try again in a moment.');
  }
}

// The build spec as plain lines, for the one hidden field that carries it into
// the notification email.
function specAsText(build) {
  const spec = (build || {}).spec || {};
  return Object.entries(spec)
    // _-prefixed keys are Shopify's convention for internal line item
    // properties; they are noise to whoever reads the email.
    .filter(([key]) => key.charAt(0) !== '_')
    .map(([key, value]) => key + ': ' + value)
    .join('\n');
}

// ─── Surviving the reload ────────────────────────────────────────────────────
// Shopify redirects back to this page after a native form post and drops the
// ?config= query string, which would lose the customer's design. Park it first
// and pick it up on the way back.

const CALLBACK_STASH_KEY = 'cb-build-before-callback';

function stashBuildForReload() {
  try {
    sessionStorage.setItem(CALLBACK_STASH_KEY, serialiseConfig());
  } catch (e) {
    // Private browsing, or storage refused. The request still sends; the
    // customer just returns to a default build.
    if (DEBUG) console.warn('[clock-builder] Could not park the build:', e);
  }
}

function takeStashedBuild() {
  try {
    const value = sessionStorage.getItem(CALLBACK_STASH_KEY);
    if (value) sessionStorage.removeItem(CALLBACK_STASH_KEY);
    return value;
  } catch (e) {
    return null;
  }
}


// ─── Materials ────────────────────────────────────────────────────────────────

function extractMaterials() {
  if (meshes.swatch1) materials.powder = meshes.swatch1.material.clone();
  if (meshes.swatch2) materials.copper = meshes.swatch2.material.clone();
  if (meshes.swatch3) materials.aluminium = meshes.swatch3.material.clone();

  // Swatch meshes can carry a baked colour attribute (e.g. AO_Shadows) which flags
  // the material to use vertex colours. The frame has no such attribute, so Three.js
  // would multiply the colour to black. Clear the flag on the extracted copies.
  ['powder', 'copper', 'aluminium'].forEach(k => {
    if (materials[k]) {
      materials[k].vertexColors = false;
      materials[k].envMapIntensity = SCENE_SETTINGS.envIntensity;
    }
  });

  applyFinishSettings();

  if (meshes.swatch1) meshes.swatch1.visible = false;
  if (meshes.swatch2) meshes.swatch2.visible = false;
  if (meshes.swatch3) meshes.swatch3.visible = false;

  setFrameMaterial('powder');
  setFrameColour('#1a1a1a');

  if (DEBUG) console.log('Materials extracted:', Object.keys(materials));
}

// Tint and surface adjustments for the textured metal finishes.
function applyFinishSettings() {
  Object.entries(FINISH_SETTINGS).forEach(([key, cfg]) => {
    const m = materials[key];
    if (!m) return;
    if (m.color) {
      m.userData._baseColor = m.userData._baseColor || m.color.clone();
      m.color.copy(m.userData._baseColor).multiply(new THREE.Color(cfg.tint));
    }
    if (cfg.metalness !== null && 'metalness' in m) m.metalness = cfg.metalness;
    if (cfg.roughness !== null && 'roughness' in m) m.roughness = cfg.roughness;
    m.needsUpdate = true;
  });
}

function setFrameMaterial(type) {
  const frame = meshes.frame;
  if (!frame || !materials[type]) return;
  frame.material = materials[type].clone();
  frame.material.needsUpdate = true;
}

function setFrameColour(hexString) {
  const frame = meshes.frame;
  if (!frame) return;
  frame.material.color.set(hexString);
  frame.material.needsUpdate = true;
}

function setFramePowderWithColour(hexString) {
  const frame = meshes.frame;
  if (!frame || !materials.powder) return;
  frame.material = materials.powder.clone();
  frame.material.color.set(hexString);
  frame.material.needsUpdate = true;
}

// ─── Views ───────────────────────────────────────────────────────────────────

function setView(view) {
  const isMobile = window.innerWidth <= 900;
  controls.autoRotate = false;
  controls.enabled = true;

  switch (view) {
    case 'front':
      animateCameraTo(new THREE.Vector3(0, 0, 3), new THREE.Vector3(0, 0, 0));
      break;
    case 'back':
      animateCameraTo(new THREE.Vector3(0, 0, isMobile ? -2.6 : -3), new THREE.Vector3(0, 0, 0));
      break;
    case 'angle':
      animateCameraTo(new THREE.Vector3(-1.5, 0, isMobile ? 2.6 : 2.2), new THREE.Vector3(0, 0, 0));
      break;
  }
}

function animateCameraTo(targetPos, targetLook) {
  const startPos = camera.position.clone();
  const start = performance.now();
  const duration = 600;

  function step(now) {
    const t = Math.min((now - start) / duration, 1);
    const ease = 1 - Math.pow(1 - t, 3);
    camera.position.lerpVectors(startPos, targetPos, ease);
    controls.target.lerp(targetLook, ease);
    controls.update();
    if (t < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

// Dolly the camera toward or away from whatever it is currently looking at,
// clamped to the same limits OrbitControls uses for scroll/pinch zoom.
function zoomBy(factor) {
  if (!camera || !controls) return;
  const target = controls.target.clone();
  const offset = camera.position.clone().sub(target);
  const distance = offset.length();
  const next = THREE.MathUtils.clamp(
    distance * factor,
    controls.minDistance,
    controls.maxDistance
  );
  if (Math.abs(next - distance) < 0.001) return;
  animateCameraTo(target.clone().add(offset.setLength(next)), target);
}

// Frame the camera on the plaque, derived from its actual world bounding box.
// Works on any model at any scale, so it needs no per-model tuning.
// Set MODEL_SETTINGS[model].plaqueCam to override manually.
function framePlaque() {
  const plaque = meshes.plaque;
  const override = modelSettings().plaqueCam;

  if (override) {
    animateCameraTo(override.pos, override.target);
    return;
  }

  if (!plaque) {
    setView('back');
    return;
  }

  const box = new THREE.Box3().setFromObject(plaque);
  const centre = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const extent = Math.max(size.x, size.y, size.z);

  // Approach from behind the clock, slightly off-axis so the plaque reads as 3D.
  const dir = new THREE.Vector3(0.38, 0.16, -1).normalize();
  const distance = Math.max(extent * 3.4, 0.45);
  const pos = centre.clone().add(dir.multiplyScalar(distance));

  animateCameraTo(pos, centre);
}

// ─── Console helpers (development only — remove before launch) ────────────────

// Orbit to an angle you like, then run _cam() to read the coordinates.
window._cam = () => console.log(
  'pos:', camera.position.toArray().map(n => +n.toFixed(2)),
  'target:', controls.target.toArray().map(n => +n.toFixed(2))
);

// Why isn't the plaque showing? Run _plaque().
window._plaque = () => {
  const p = meshes.plaque;
  if (!p) {
    console.log('No plaque mesh mapped. Expected an object named', MESH_NAMES.plaque);
    return;
  }
  const box = new THREE.Box3().setFromObject(p);
  const centre = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const r = n => +n.toFixed(3);

  let ancestorHidden = null;
  let node = p.parent;
  while (node) {
    if (node.visible === false) ancestorHidden = node.name || '(unnamed)';
    node = node.parent;
  }

  console.log('model:            ', config.model);
  console.log('mesh name:        ', p.name);
  console.log('config.plaque:    ', config.plaque);
  console.log('mesh.visible:     ', p.visible);
  console.log('hidden ancestor:  ', ancestorHidden || 'none');
  console.log('world centre:     ', centre.toArray().map(r));
  console.log('world size:       ', size.toArray().map(r));
  const g = p.geometry;
  const m = p.material;
  console.log('vertices:         ', g && g.attributes.position ? g.attributes.position.count : 'none');
  console.log('has UVs:          ', !!(g && g.attributes.uv));
  console.log('material:         ', m && m.type);
  console.log('has canvas map:   ', !!(m && m.map));
  console.log('side:             ', m && (m.side === THREE.DoubleSide ? 'Double' : m.side === THREE.BackSide ? 'Back' : 'Front'));
  console.log('transparent:      ', m && m.transparent, '| opacity:', m && m.opacity);
  console.log('depthTest:        ', m && m.depthTest, '| renderOrder:', p.renderOrder);
  console.log('frustumCulled:    ', p.frustumCulled);
  console.log('world scale:      ', p.getWorldScale(new THREE.Vector3()).toArray().map(r));
  console.log('camera pos:       ', camera.position.toArray().map(r));
  console.log('controls target:  ', controls.target.toArray().map(r));
  console.log('camera → plaque:  ', r(camera.position.distanceTo(centre)));
};

// Jump the camera straight to the plaque, ignoring any override.
window._plaqueFrame = () => {
  const saved = modelSettings().plaqueCam;
  modelSettings().plaqueCam = null;
  framePlaque();
  modelSettings().plaqueCam = saved;
};

// Nudge the plaque outward from the back panel until it stops being occluded.
// e.g. _plaqueNudge(0.006). Prints the value to copy into MODEL_SETTINGS.
window._plaqueNudge = (v) => {
  modelSettings().plaqueOffset = v;
  applyPlaqueOffset();
  console.log(config.model, 'plaqueOffset:', v, '| local z:', +meshes.plaque.position.z.toFixed(4));
};

// Hide everything except the plaque. Run again to restore.
// If the plaque appears once isolated, it was being occluded by other geometry.
let _isolated = false;
window._plaqueIsolate = () => {
  const root = scene.getObjectByName('clock_root');
  if (!root) return console.log('No model loaded.');
  _isolated = !_isolated;
  root.traverse((o) => {
    if (o.isMesh) o.visible = _isolated ? (o === meshes.plaque) : true;
  });
  // Swatches stay hidden either way
  ['swatch1', 'swatch2', 'swatch3'].forEach(k => {
    if (meshes[k]) meshes[k].visible = false;
  });
  if (!_isolated && meshes.plaque) meshes.plaque.visible = config.plaque;
  console.log(_isolated ? 'Plaque isolated — everything else hidden.' : 'All meshes restored.');
};

// Force the plaque to draw on top of everything, ignoring depth.
// If it appears, the plaque is behind or coplanar with other geometry.
window._plaqueOnTop = (on = true) => {
  const p = meshes.plaque;
  if (!p) return console.log('No plaque mesh.');
  p.material.depthTest = !on;
  p.material.needsUpdate = true;
  p.renderOrder = on ? 999 : 0;
  console.log('depthTest:', p.material.depthTest, '| renderOrder:', p.renderOrder);
};

// If the plaque text reads backwards or upside down, try each in turn:
// _plaqueTx('rotate180') / _plaqueTx('mirror') / _plaqueTx('none')
window._plaqueTx = (mode) => {
  modelSettings().plaqueTransform = mode;
  updatePlaqueTexture();
  console.log(config.model, 'plaqueTransform:', mode);
};

// Adjust a metal finish live, e.g.
//   _finish('copper', { tint: '#e8913f' })
//   _finish('copper', { roughness: 0.3, metalness: 1 })
// tint multiplies the brushed texture, so '#ffffff' is the original.
window._finish = (name, changes = {}) => {
  if (!FINISH_SETTINGS[name]) {
    console.log('Unknown finish. Options:', Object.keys(FINISH_SETTINGS).join(', '));
    return;
  }
  Object.assign(FINISH_SETTINGS[name], changes);
  applyFinishSettings();
  // Re-apply to the frame if this finish is the one on screen
  if (config.material === name) setFrameMaterial(name);
  console.log(name, JSON.stringify(FINISH_SETTINGS[name]));
};

// ─── Per-object material tuning ──────────────────────────────────────────────
// List every mesh in the loaded model with its material, so you can see which
// objects share one. Optional substring filter: _mats('backcover').
window._mats = (filter = '') => {
  const root = scene && scene.getObjectByName('clock_root');
  if (!root) return console.log('No model loaded.');

  // Count how many meshes point at each material instance, so the table says
  // what a material edit would affect.
  const users = new Map();
  root.traverse((o) => {
    if (!o.isMesh || !o.material) return;
    users.set(o.material.uuid, (users.get(o.material.uuid) || 0) + 1);
  });

  const want = filter.toLowerCase();
  const rows = [];
  root.traverse((o) => {
    if (!o.isMesh || !o.material) return;
    if (want && !o.name.toLowerCase().includes(want)) return;
    const m = o.material;
    const n = v => (typeof v === 'number' ? +v.toFixed(2) : '—');
    rows.push({
      mesh: o.name,
      material: m.name || '(unnamed)',
      colour: '#' + m.color.getHexString(),
      roughness: n(m.roughness),
      metalness: n(m.metalness),
      'shared by': users.get(m.uuid)
    });
  });

  if (!rows.length) return console.log('No mesh name contains "' + filter + '".');
  console.table(rows);
};

// Change one object's material without touching the others that share it.
// glTF materials are shared instances, so setting the colour on "Black plastic"
// would repaint every part using it — the first call here clones the material
// onto the matched meshes alone.
//
//   _mat('BackCover', { colour: '#3a3a3a' })
//   _mat('BackCover', { colour: '#3a3a3a', roughness: 0.6 })
//
// Matching is a case-insensitive substring, so a partial name is fine. Nothing
// here persists: a model reload (changing size or artwork) restores the GLB's
// own material, so once a value looks right it belongs in Blender.
window._mat = (meshName, changes = {}) => {
  const root = scene && scene.getObjectByName('clock_root');
  if (!root) return console.log('No model loaded.');
  if (!meshName) return console.log('Usage: _mat(\'BackCover\', { colour: \'#3a3a3a\' })');

  const want = String(meshName).toLowerCase();
  const hits = [];
  root.traverse((o) => {
    if (o.isMesh && o.material && o.name.toLowerCase().includes(want)) hits.push(o);
  });
  if (!hits.length) {
    return console.log('No mesh name contains "' + meshName + '". Run _mats() to list them.');
  }

  hits.forEach((o) => {
    if (!o.userData.cbMatCloned) {
      o.material = o.material.clone();
      o.userData.cbMatCloned = true;
    }
    const m = o.material;
    const colour = changes.colour ?? changes.color;
    if (colour) m.color.set(colour);
    ['roughness', 'metalness', 'opacity'].forEach((k) => {
      if (typeof changes[k] === 'number') m[k] = changes[k];
    });
    if (typeof changes.opacity === 'number') m.transparent = changes.opacity < 1;
    m.needsUpdate = true;
  });

  const m = hits[0].material;
  const n = v => (typeof v === 'number' ? +v.toFixed(2) : null);
  console.log(
    hits.length + ' mesh(es): ' + hits.map(o => o.name).join(', '),
    JSON.stringify({ colour: '#' + m.color.getHexString(), roughness: n(m.roughness), metalness: n(m.metalness) })
  );
};

// Push the art layers further back along Z, to test whether the acne is simply
// the dial-to-artwork gap being too small for the shadow map to resolve.
// e.g. _artOffset(0.01). Runs in world units — 0.01 ≈ 2.7mm at scale 1.2.
// If the artefacts clear at some value, the real fix is to increase that
// separation in Blender rather than carry an offset here.
const _artBaseZ = new Map();
window._artOffset = (v = 0) => {
  const root = scene.getObjectByName('clock_root');
  if (!root) return console.log('No model loaded.');
  let n = 0;
  root.traverse((o) => {
    if (!o.isMesh || !/^art_/i.test(o.name)) return;
    if (!_artBaseZ.has(o.uuid)) _artBaseZ.set(o.uuid, o.position.z);
    o.position.z = _artBaseZ.get(o.uuid) - v;
    n++;
  });
  if (lights.key) lights.key.shadow.needsUpdate = true;
  console.log('offset', v, 'applied to', n, 'art meshes (0 restores)');
};

// Tune the rear lighting from the console while looking at the back:
//   _back(2.2)                 — brighter
//   _back(1.6, 40, 15)         — rake it further round and flatter
// Angles are degrees from the camera. Flat-on (0, 0) deliberately looks worse:
// the pattern reads because the light crosses it at an angle.
window._back = (intensity, yaw, pitch) => {
  if (typeof intensity === 'number') SCENE_SETTINGS.backLight = intensity;
  if (typeof yaw === 'number') SCENE_SETTINGS.backLightYaw = yaw;
  if (typeof pitch === 'number') SCENE_SETTINGS.backLightPitch = pitch;
  console.log('backLight:', SCENE_SETTINGS.backLight,
    '| backLightYaw:', SCENE_SETTINGS.backLightYaw,
    '| backLightPitch:', SCENE_SETTINGS.backLightPitch);
  console.log('Paste into "scene" in #cb-settings to keep it:',
    JSON.stringify({
      backLight: SCENE_SETTINGS.backLight,
      backLightYaw: SCENE_SETTINGS.backLightYaw,
      backLightPitch: SCENE_SETTINGS.backLightPitch
    }));
};

// Report the shadow system's actual state.
window._shadow = () => {
  const k = lights.key;
  if (!k) return console.log('No key light.');
  const c = k.shadow.camera;
  const r = n => +n.toFixed(4);
  console.log('renderer.shadowMap.enabled:', renderer.shadowMap.enabled);
  console.log('key.castShadow:            ', k.castShadow);
  console.log('key.intensity:             ', k.intensity);
  console.log('key position / distance:   ', k.position.toArray(), r(k.position.length()));
  console.log('shadow camera near / far:  ', r(c.near), r(c.far));
  console.log('shadow camera extent:      ', r(c.left), r(c.right), r(c.top), r(c.bottom));
  console.log('normalBias (applied):      ', r(k.shadow.normalBias));
  console.log('bias (applied):            ', r(k.shadow.bias));
  console.log('radius:                    ', k.shadow.radius);
  console.log('mapSize:                   ', k.shadow.mapSize.x, k.shadow.mapSize.y);
  console.log('SETTINGS normalBias:       ', SCENE_SETTINGS.shadowNormalBias, '(× scale', modelSettings().scale + ')');
};

// Set the shadow bias directly, bypassing the scale multiplier and without
// touching anything else in the scene. Use this to isolate bias behaviour.
window._bias = (normal, b) => {
  const k = lights.key;
  if (!k) return;
  if (normal !== undefined) k.shadow.normalBias = normal;
  if (b !== undefined) k.shadow.bias = b;
  k.shadow.needsUpdate = true;
  console.log('normalBias:', k.shadow.normalBias, '| bias:', k.shadow.bias);
};

// List which meshes cast shadows, to check the shadowNoCast rule is matching
// what you expect rather than guessing at names.
window._casters = () => {
  const root = scene.getObjectByName('clock_root');
  if (!root) return console.log('No model loaded.');
  const on = [], off = [];
  root.traverse((o) => { if (o.isMesh) (o.castShadow ? on : off).push(o.name); });
  console.log('CASTS shadows:', on);
  console.log('does NOT cast:', off);
};

// Tune the lighting live, e.g. _scene({ exposure: 1.1, ambient: 0.4, key: 2.5 })
// Prints the full settings object to copy into SCENE_SETTINGS.
window._scene = (changes = {}) => {
  Object.assign(SCENE_SETTINGS, changes);
  renderer.toneMapping = TONE_MAPPING[SCENE_SETTINGS.toneMapping] || THREE.NeutralToneMapping;
  renderer.toneMappingExposure = SCENE_SETTINGS.exposure;
  if (scene.background) scene.background.set(SCENE_SETTINGS.background);
  renderer.shadowMap.enabled = SCENE_SETTINGS.shadows;
  if (lights.key) {
    lights.key.castShadow = SCENE_SETTINGS.shadows;
    lights.key.shadow.bias = SCENE_SETTINGS.shadowBias;
    lights.key.shadow.normalBias = SCENE_SETTINGS.shadowNormalBias;
    lights.key.shadow.radius = SCENE_SETTINGS.shadowSoftness;
    lights.key.position.set(...SCENE_SETTINGS.keyPosition);
    fitShadowCamera();
  }
  const clockRoot = scene.getObjectByName('clock_root');
  if (clockRoot) clockRoot.traverse((o) => {
    if (o.isMesh) o.castShadow = SCENE_SETTINGS.shadows && !SCENE_SETTINGS.shadowNoCast.test(o.name);
  });
  applyZoomLimits();
  if (lights.ambient) lights.ambient.intensity = SCENE_SETTINGS.ambient;
  if (lights.key)     lights.key.intensity     = SCENE_SETTINGS.key;
  if (lights.fill)    lights.fill.intensity    = SCENE_SETTINGS.fill;
  if (lights.rim)     lights.rim.intensity     = SCENE_SETTINGS.rim;
  if (lights.key)     lights.key.shadow.radius = SCENE_SETTINGS.shadowSoftness;
  const root = scene.getObjectByName('clock_root');
  if (root) root.traverse((o) => {
    if (!o.isMesh) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach((m) => {
      if (!m) return;
      if (m.userData && m.userData._isArt) {
        if (m.userData._baseColor && m.color) {
          m.color.copy(m.userData._baseColor).multiplyScalar(SCENE_SETTINGS.artDarken);
        }
        if ('envMapIntensity' in m) m.envMapIntensity = SCENE_SETTINGS.artEnv;
        if ('roughness' in m) m.roughness = SCENE_SETTINGS.artRoughness;
      } else if ('envMapIntensity' in m) {
        m.envMapIntensity = SCENE_SETTINGS.envIntensity;
      }
      if (/glass/i.test(o.name) || /glass/i.test(m.name || '')) {
        m.opacity = SCENE_SETTINGS.glassOpacity;
      }
      m.needsUpdate = true;
    });
  });
  console.log('SCENE_SETTINGS:', JSON.stringify(SCENE_SETTINGS, null, 2));

};

// Tune dial text live, e.g.
//   _face({ subSize: 28, subY: 100, subTrack: 2 })
// Keys: subY/subSize/subTrack, labelY/labelSize/labelTrack. The clock title was
// removed, so there are no title* keys any more.
window._face = (changes = {}) => {
  Object.assign(modelSettings().faceText, changes);
  updateFaceTexture();
  console.log(config.model, 'faceText:', JSON.stringify(modelSettings().faceText));
  console.log('Paste into #cb-settings in clock-builder.liquid under models.'
    + config.model + '.faceText to keep it.');
};

// ─── Render loop ─────────────────────────────────────────────────────────────

// Rake a light across the rear casing, fading in as the camera comes round the
// back. The clock faces +Z, so a camera at -Z is looking at the back.
//
// Offsetting the light from the camera is the whole point: the rear pattern is
// gloss on matt black, which carries almost no colour contrast, so it only
// reads through specular response. A light sharing the camera's axis puts one
// hotspot in the middle and leaves the rest flat — the same reason a camera
// flash makes everything look dead.
const _backLightDir = new THREE.Vector3();
const _backLightAxis = new THREE.Vector3();

function updateBackLight() {
  const light = lights.back;
  if (!light) return;

  // How far round the back we are: 0 anywhere in front, rising to 1 dead astern.
  const toCamera = _backLightDir.copy(camera.position).sub(controls.target);
  const distance = toCamera.length() || 1;
  const backness = Math.max(0, -toCamera.z / distance);

  light.intensity = SCENE_SETTINGS.backLight * backness * backness;
  if (light.intensity <= 0.001) return;   // nothing to position

  // Start at the camera, then swing the direction up and to one side so the
  // light grazes the surface rather than staring straight at it.
  const yaw = THREE.MathUtils.degToRad(SCENE_SETTINGS.backLightYaw);
  const pitch = THREE.MathUtils.degToRad(SCENE_SETTINGS.backLightPitch);

  toCamera.applyAxisAngle(_backLightAxis.set(0, 1, 0), yaw);
  // Pitch about the axis perpendicular to both up and the view direction, so
  // the light lifts regardless of which way the camera has been dragged.
  _backLightAxis.set(0, 1, 0).cross(toCamera).normalize();
  if (_backLightAxis.lengthSq() > 0) toCamera.applyAxisAngle(_backLightAxis, -pitch);

  light.position.copy(controls.target).add(toCamera);
  light.target.position.copy(controls.target);
  light.target.updateMatrixWorld();
}

function animate() {
  requestAnimationFrame(animate);
  controls.update();
  updateBackLight();
  renderer.render(scene, camera);
}

function onResize() {
  const container = document.getElementById('cb-viewer');
  if (!container) return;
  const w = container.clientWidth;
  const h = container.clientHeight;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
}

// ─── Config state ─────────────────────────────────────────────────────────────

const config = {
  model: 'giant',
  artwork: 'standard',
  material: 'midnight-black',
  ralColour: null,
  // Face text is opt-in, like the plaque: the charge only applies once it is
  // switched on, so an untouched face costs nothing even if a field was typed
  // into and then cleared.
  faceTextOn: false,
  faceSub: '',
  weatherLabel: '',
  faceExtras: '',
  textColour: '#1a1a1a',
  font: 'mono',
  scene: 'default',
  plaque: false,
  plaqueType: 'typeface',
  plaqueMessage: '',
  plaqueAlign: 'left',
  plaqueFont: 'mono',
  plaqueImage: null,
  plaqueImageEl: null,
  additionalNotes: ''
};

// ─── Pricing ─────────────────────────────────────────────────────────────────
// Prices are NOT defined here. They are rendered into the page by Liquid, which
// means they arrive already converted to the visitor's market currency — from
// the Shopify Markets catalog when the option is mapped to a product, or from
// the static per-currency table in the section otherwise. See the
// #cb-pricing JSON block in clock-builder.liquid.
//
// The fallback below is GBP and exists only so the builder still runs if that
// block is missing or malformed. It is not the source of truth and will drift.
const PRICES_FALLBACK = {
  model: { weather: 495, giant: 995 },
  material: {
    'custom-ral': 250,
    copper: 30,
    aluminium: 30,
    'midnight-black': 0,
    'summer-blue': 0,
    'cloudy-grey': 0
  },
  faceText: { weather: 225, giant: 375 },
  plaque: 45,
  fullCustom: { weather: 5995, giant: 5995 },
  commissionDeposit: 0
};

const CURRENCY_FALLBACK = { code: 'GBP', locale: 'en-GB' };

// Read the pricing block Liquid rendered. Amounts come in as currency
// subunits (Shopify's convention: 5995 GBP is 599500), so they are divided
// down once here and everything downstream works in major units.
function readPricing() {
  const el = document.getElementById('cb-pricing');
  if (!el) {
    console.warn('[clock-builder] No #cb-pricing block found — falling back to GBP list prices.');
    return { prices: PRICES_FALLBACK, currency: CURRENCY_FALLBACK, source: 'fallback', depositSource: 'static' };
  }

  try {
    const data = JSON.parse(el.textContent);
    const div = (n) => (typeof n === 'number' ? n / 100 : n);
    const divMap = (obj) => Object.fromEntries(
      Object.entries(obj || {}).map(([k, v]) => [k, div(v)])
    );

    return {
      prices: {
        model: divMap(data.prices.model),
        material: divMap(data.prices.material),
        // Priced per clock size since the Giant's dial is a bigger job. A plain
        // number here is an older #cb-pricing block, so it is widened to both
        // sizes rather than left to read as undefined everywhere downstream.
        faceText: typeof data.prices.faceText === 'number'
          ? { weather: div(data.prices.faceText), giant: div(data.prices.faceText) }
          : divMap(data.prices.faceText),
        plaque: div(data.prices.plaque),
        fullCustom: divMap(data.prices.fullCustom),
        commissionDeposit: div(data.prices.commissionDeposit || 0)
      },
      currency: {
        code: data.currency || CURRENCY_FALLBACK.code,
        locale: data.locale || CURRENCY_FALLBACK.locale
      },
      market: data.market || null,
      source: data.source || 'unknown',
      depositSource: data.depositSource || 'static'
    };
  } catch (e) {
    console.error('[clock-builder] Could not parse #cb-pricing — falling back to GBP:', e);
    return { prices: PRICES_FALLBACK, currency: CURRENCY_FALLBACK, source: 'fallback', depositSource: 'static' };
  }
}

const PRICING = readPricing();
const PRICES = PRICING.prices;
const CURRENCY = PRICING.currency;
// Where the deposit figure came from: 'shopify' (the deposit product, so the
// page and the cart cannot disagree), 'static' (the s_deposit table, which can),
// or 'handle-not-found' (a typo that fell back to the table in silence).
const DEPOSIT_SOURCE = PRICING.depositSource;

// Format an amount in the visitor's market currency. Whole amounts show no
// decimals, part amounts show two, so a £5,995 commission reads cleanly while
// a £3,122.50 deposit stays exact.
function formatMoney(amount) {
  const n = Number(amount) || 0;
  const fraction = Math.abs(n % 1) > 0.0001 ? 2 : 0;
  try {
    return new Intl.NumberFormat(CURRENCY.locale, {
      style: 'currency',
      currency: CURRENCY.code,
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction
    }).format(n);
  } catch (e) {
    // An unrecognised currency or locale must not take the price display down.
    return CURRENCY.code + ' ' + n.toFixed(fraction);
  }
}

function isFullCustom() {
  return config.artwork === 'custom';
}

// Whether the dial carries customer text at all. Opt-in via the step 4 toggle,
// so emptying the fields is not the same as turning the option off.
// What the personalised face costs for the clock currently selected. Zero on a
// commission, which already covers the bespoke face.
function faceTextPrice() {
  if (isFullCustom()) return 0;
  const byModel = PRICES.faceText;
  if (typeof byModel === 'number') return byModel;
  return (byModel && byModel[config.model]) || 0;
}

function faceTextOn() {
  return config.faceTextOn === true;
}

// A commission's price covers the bespoke face, so the face-text charge only
// applies to a standard build.
function faceTextCharged() {
  return faceTextOn() && !isFullCustom();
}

function calcTotal() {
  let total;

  if (isFullCustom()) {
    // The commission price covers the clock, the bespoke face and the bespoke
    // artwork, so the standard face-text charge does not apply on top.
    total = PRICES.fullCustom[config.model];
  } else {
    total = PRICES.model[config.model];
    if (faceTextOn()) total += faceTextPrice();
  }

  // Charged on every build, commission or not.
  total += PRICES.material[config.material];
  if (config.plaque) total += PRICES.plaque;
  return total;
}

// The deposit taken up front and the balance due on completion.
function depositAmount() {
  if (depositIsFixed()) {
    // Never ask for more than the order is worth, however the options land.
    return Math.min(PRICES.commissionDeposit, calcTotal());
  }
  return Math.round(calcTotal() * DEPOSIT_PERCENT / 100 * 100) / 100;
}

function balanceAmount() {
  return Math.round((calcTotal() - depositAmount()) * 100) / 100;
}

// Is the workshop open right now? Judged in UK time whatever the visitor's
// own timezone, since that is where the phone is answered.
function isBusinessHours() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', weekday: 'short', hour: 'numeric', hour12: false
  }).formatToParts(new Date());
  const dayName = (parts.find(p => p.type === 'weekday') || {}).value;
  const hour = parseInt((parts.find(p => p.type === 'hour') || {}).value, 10);
  const dayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(dayName);
  if (dayIndex === -1 || isNaN(hour)) return false;
  const h = CUSTOMER_SERVICE.hours;
  return h.days.includes(dayIndex) && hour >= h.open && hour < h.close;
}

// Fill every element carrying a data-price-key from PRICES, so no currency
// figure is hardcoded in the markup. Runs once at boot and again whenever the
// model changes, since some prices are per model.
function renderStaticPrices() {
  document.querySelectorAll('[data-price-key]').forEach(el => {
    const value = el.dataset.priceKey
      .split('.')
      .reduce((obj, key) => (obj == null ? undefined : obj[key]), PRICES);

    if (typeof value !== 'number') {
      console.warn('[clock-builder] No price for key', el.dataset.priceKey);
      return;
    }

    if (value === 0) { el.textContent = 'Included'; return; }

    const money = formatMoney(value);
    switch (el.dataset.priceFormat) {
      case 'from': el.textContent = 'From ' + money; break;
      case 'plus': el.textContent = '+' + money; break;
      default:     el.textContent = money;
    }
  });
}

function updatePrice() {
  const total = calcTotal();
  const el = document.getElementById('cb-total');
  if (el) {
    el.textContent = formatMoney(total);
  }
  // The face charge differs by clock size, so the figure on the toggle is a
  // price like any other and goes stale the moment the size changes. It lives
  // here rather than in the size handler so that every path which can move
  // money - a size change, restoring a saved build - refreshes it.
  updateFaceTextPriceLabel();
  // The deposit is a share of the total, so it moves whenever the total does.
  if (isFullCustom()) updateCommissionFigures();
}

// ─── Step navigation ─────────────────────────────────────────────────────────

const stepBtns = document.querySelectorAll('.cb-step');
const panes = document.querySelectorAll('.cb-pane');

// The one source of truth for which step is showing. The footer's Back/Next
// read it, so there is no per-pane nav to keep in sync.
let currentStep = 0;

// Label for the forward button at each step, so it says where it is going.
// The last entry turns it into the cart action.
const STEP_NEXT_LABELS = [
  'Next: Artwork \u2192',
  'Next: Frame finish \u2192',
  'Next: Face text \u2192',
  'Next: Plaque \u2192',
  'Next: Preview \u2192',
  'Next: Review \u2192',
  'Add to Cart'
];

// Changing step used to leave the viewport wherever it was, which on mobile
// means landing at the bottom of the previous step and scrolling up by hand.
function scrollStepIntoView() {
  // The panel scrolls independently on desktop.
  const content = document.querySelector('.cb-panel__content');
  if (content) content.scrollTop = 0;

  // Only mobile scrolls the PAGE. Above the 900px breakpoint the two-column
  // layout is a fixed-height box with its own scrolling panel, so moving the
  // page served no purpose and actively broke things: it scrolled the theme
  // header away, which lifted the sticky footer ~80px up the viewport and put
  // it under the floating chat launcher — right where the Next button is.
  if (window.innerWidth > 900) return;

  const target = document.querySelector('.cb-steps') || document.querySelector('.cb-panel');
  if (!target) return;

  const top = target.getBoundingClientRect().top;
  // Already at or above the top of the viewport: nothing to do.
  if (top >= 0 && top < 8) return;

  try {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (e) {
    target.scrollIntoView(true);
  }
}

// The narrow bar can't fit "Next: Face text" without truncating, and a third
// party chat launcher may be sitting in the corner as well, so mobile gets the
// bare action.
const STEP_NEXT_LABELS_SHORT = [
  'Next \u2192', 'Next \u2192', 'Next \u2192', 'Next \u2192',
  'Next \u2192', 'Next \u2192', 'Add to Cart'
];

// A full custom build goes to the enquiry form rather than the cart unless the
// client has chosen to sell it — either as a fixed deposit or outright.
function commissionNeedsEnquiry() {
  return isFullCustom() && commissionMode() === 'enquiry';
}

function isNarrowViewport() {
  try {
    return window.matchMedia('(max-width: 700px)').matches;
  } catch (e) {
    return window.innerWidth <= 700;
  }
}

function updateFooterNav() {
  const back = document.getElementById('cb-nav-back');
  const next = document.getElementById('cb-nav-next');
  const last = STEP_NEXT_LABELS.length - 1;
  const labels = isNarrowViewport() ? STEP_NEXT_LABELS_SHORT : STEP_NEXT_LABELS;

  // Collapsed on step 1, not just disabled. `display: none` removes the box
  // entirely, so the share button and Next close the gap; `visibility: hidden`,
  // which this used before, kept the box and left the share icon sitting
  // against an empty 68px space. The trade-off is that the share button shifts
  // ~74px when Back appears on step 2 — accepted deliberately.
  if (back) {
    back.style.display = currentStep === 0 ? 'none' : '';
    back.disabled = false;
    back.style.visibility = '';
  }
  if (next) {
    const isLast = currentStep === last;
    // On the last step the button says what it will actually do, which for a
    // commission is open an enquiry rather than take payment.
    let label = labels[currentStep] || 'Next \u2192';
    if (isLast && isFullCustom()) {
      if (commissionMode() === 'enquiry') label = 'Request your commission';
      else if (commissionMode() === 'deposit') {
        label = isNarrowViewport()
          ? 'Pay deposit'
          : 'Reserve with ' + formatMoney(depositAmount());
      }
    }
    next.textContent = label;
    next.classList.toggle('cb-btn--cart', isLast);
  }
}

// Rotating a phone crosses the breakpoint, so the labels have to follow.
window.addEventListener('resize', () => { updateFooterNav(); });

function goToStep(n) {
  currentStep = n;
  updateFooterNav();
  scrollStepIntoView();

  stepBtns.forEach((s, i) => {
    s.classList.remove('active', 'done');
    if (i === n) s.classList.add('active');
    if (i < n) s.classList.add('done');
  });
  panes.forEach((p, i) => {
    p.classList.toggle('active', i === n);
  });

  if (n === 3) {
    setView('front');
    document.querySelectorAll('.cb-view-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.view === 'front');
    });
  } else if (n === 5) {
    setView('front');
    document.querySelectorAll('.cb-shot').forEach(s => s.classList.remove('active'));
    const frontShot = document.querySelector('.cb-shot[data-shot="front"]');
    if (frontShot) frontShot.classList.add('active');
    const backShot = document.getElementById('cb-shot-back');
    if (backShot) backShot.style.display = config.plaque ? 'block' : 'none';
    document.querySelectorAll('.cb-view-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.view === 'front');
    });
  } else if (n === 6) {
    setView('front');
    buildPriceBreakdown();
    document.querySelectorAll('.cb-view-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.view === 'front');
    });
  }
}

stepBtns.forEach((btn, i) => {
  btn.addEventListener('click', () => goToStep(i));
});

// A single Back/Next pair in the sticky footer. Keeping the primary action
// pinned there means no amount of pane content can push it out of reach.
const navBackEl = document.getElementById('cb-nav-back');
if (navBackEl) {
  navBackEl.addEventListener('click', () => {
    if (currentStep > 0) goToStep(currentStep - 1);
  });
}

const navNextEl = document.getElementById('cb-nav-next');
if (navNextEl) {
  navNextEl.addEventListener('click', () => {
    const last = STEP_NEXT_LABELS.length - 1;
    if (currentStep < last) goToStep(currentStep + 1);
    else addToCart();
  });
}

// Cart integration is still open \u2014 see cart-integration-options.md. This is
// the single place to wire it, and until then it reports what would be sent.
// ─── Cart ────────────────────────────────────────────────────────────────────
// Option A from cart-integration-options.md: one product per clock model, each
// paid option added as its own line item. Variant ids live in the #cb-settings
// block so they can be filled in without touching this file.
//
// Prices come from Shopify, not from here — the builder's total is a preview of
// what the catalog will charge. Those two can only agree if each option's
// variant price matches the #cb-pricing figure for the same market.

const CART = applyOverrides({
  // Each clock has a default variant and, where the product has them, a variant
  // per frame material. Copper and Chrome are variants of the regular clock, so
  // choosing one swaps the variant rather than adding a second line.
  model: {
    weather: { default: 0, byMaterial: { copper: 0, aluminium: 0 } },
    giant: { default: 0, byMaterial: {} }
  },
  // Used only for options that have no variant of their own.
  addons: {
    material: { 'custom-ral': 0, copper: 0, aluminium: 0 },
    // The personalised clock face is a separate product per size.
    faceText: { weather: 0, giant: 0 },
    // The plaque product has a variant per type, matching the builder's toggle.
    plaque: { typeface: 0, handwritten: 0 },
    commission: 0,
    commissionDeposit: 0
  },
  // 'enquiry'  — opens the enquiry form, takes no payment
  // 'deposit'  — adds the fixed deposit product; balance invoiced later
  // 'full'     — charges the whole commission at checkout
  commissionMode: 'enquiry',
  redirectTo: '/cart'
}, SETTINGS.cart);

function commissionMode() {
  const mode = CART.commissionMode;
  if (mode === 'deposit' || mode === 'full') return mode;
  return 'enquiry';
}

function variantId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// The variant for the chosen model and material. Returns the material-specific
// variant where the product has one, otherwise the model's default.
function modelVariant() {
  const entry = CART.model[config.model] || {};
  const byMaterial = entry.byMaterial || {};
  return variantId(byMaterial[config.material]) || variantId(entry.default);
}

// True when the material is already covered by the variant, so adding an
// add-on line for it would charge the uplift twice.
function materialIsVariant() {
  const entry = CART.model[config.model] || {};
  return !!variantId((entry.byMaterial || {})[config.material]);
}

// Build the line items for the current configuration. Returns { items, missing }
// so the caller can refuse rather than send a half-order to checkout.
function buildCartItems() {
  const items = [];
  const missing = [];
  const full = isFullCustom();

  const add = (id, label, properties) => {
    const vid = variantId(id);
    if (!vid) { missing.push(label); return; }
    const item = { id: vid, quantity: 1 };
    if (properties) item.properties = properties;
    items.push(item);
  };

  // The base clock carries the whole specification, so the workshop can read
  // one line rather than piecing it together from several.
  const props = buildLineItemProperties();
  // Underscore-prefixed: still on the order for the workshop, but not rendered
  // in the cart or the confirmation email, where a 1,000-character URL wraps
  // over a dozen lines and buries the actual specification. The customer gets
  // their copy from Save & share, and it is on the cart attributes too.
  props['_Your design'] = getSaveURL();
  // A leading underscore hides a property from the customer's cart and order
  // confirmation while keeping it on the order for staff and for restoring the
  // exact build.
  props['_config'] = serialiseConfig();

  // Deposit mode: the cart holds the deposit product and NOTHING else. Not the
  // clock, not the add-ons. The deposit is a payment against the whole order,
  // so anything else in the cart would be charged on top of it — a £3,000
  // deposit plus a £995 clock would bill £3,995. The balance, which is the rest
  // of the commission plus the frame and the plaque, is invoiced after the
  // consultation. The specification rides on the deposit line so the workshop
  // still has it.
  if (full && commissionMode() === 'deposit') {
    props['Order total'] = formatMoney(calcTotal());
    props['Balance due on completion'] = formatMoney(balanceAmount());
    add(CART.addons.commissionDeposit, 'commission deposit', props);
    return { items: items, missing: missing };
  }

  add(modelVariant(), 'clock model (' + config.model + ', ' + config.material + ')', props);

  // The commission replaces the standard face-text charge but not the frame or
  // the plaque, matching how calcTotal() prices it.
  if (full) {
    add(CART.addons.commission, 'full custom commission');
  } else if (faceTextOn()) {
    add((CART.addons.faceText || {})[config.model], 'personalised clock face (' + config.model + ')');
  }

  // Only charge the finish separately when the variant didn't already cover it.
  if (PRICES.material[config.material] > 0 && !materialIsVariant()) {
    add(CART.addons.material[config.material], 'frame finish (' + config.material + ')');
  }

  if (config.plaque) {
    const byType = CART.addons.plaque || {};
    add(byType[config.plaqueType], 'personalised plaque (' + config.plaqueType + ')');
  }

  return { items: items, missing: missing };
}

function cartConfigured() {
  return variantId(CART.model.weather.default) || variantId(CART.model.giant.default);
}

// In deposit mode the page shows prices.commissionDeposit while the cart
// charges whatever the deposit product costs. If those disagree the customer
// is quoted one figure and billed another, so say so loudly at boot rather
// than discovering it from a complaint.
function warnOnDepositMismatch() {
  if (commissionMode() !== 'deposit') return;
  if (!depositIsFixed()) {
    console.warn('[clock-builder] commissionMode is "deposit" but prices.commissionDeposit '
      + 'is 0 for ' + CURRENCY.code + ', so the page is showing a percentage of the total '
      + 'while the cart would charge the deposit product\u2019s price. Set s_deposit for '
      + 'every currency in the #cb-pricing block.');
    return;
  }
  if (!variantId(CART.addons.commissionDeposit)) {
    console.warn('[clock-builder] commissionMode is "deposit" but no deposit variant id is '
      + 'set, so a commission cannot be ordered. Set cart.addons.commissionDeposit.');
    return;
  }
  if (DEPOSIT_SOURCE === 'handle-not-found') {
    console.error('[clock-builder] h_deposit points at a handle that does not exist, so the '
      + 'page has fallen back to the s_deposit table and is quoting '
      + formatMoney(PRICES.commissionDeposit) + ' in ' + CURRENCY.code
      + ' while the cart charges the deposit product\u2019s own price. Fix the handle.');
    return;
  }
  if (DEPOSIT_SOURCE !== 'shopify') {
    console.warn('[clock-builder] The deposit is coming from the static s_deposit table, not '
      + 'the deposit product, so ' + formatMoney(PRICES.commissionDeposit) + ' in '
      + CURRENCY.code + ' is only right if it happens to match that product\u2019s price in '
      + 'this market. Set h_deposit to the deposit product\u2019s handle to make them one '
      + 'figure.');
    return;
  }
  console.log('[clock-builder] Deposit mode: ' + formatMoney(PRICES.commissionDeposit)
    + ' in ' + CURRENCY.code + ', read from the deposit product.');
}

async function addToCart() {
  const btn = document.getElementById('cb-nav-next');
  const setBtn = (text, disabled) => {
    if (!btn) return;
    btn.textContent = text;
    btn.disabled = !!disabled;
  };

  // A commission is a conversation, not a checkout. A storefront cart cannot
  // take a percentage deposit on a total that moves with the frame and plaque
  // choices, and the page promises a deposit — so route it to the enquiry form
  // rather than charging the full amount. cart.commissionMode in #cb-settings
  // switches this to 'deposit' (a fixed deposit product, balance invoiced) or
  // 'full' (the whole commission at checkout).
  if (commissionNeedsEnquiry()) {
    openCallbackModal();
    return;
  }

  if (!cartConfigured()) {
    console.warn('[clock-builder] No cart variant ids set — add them to cart.variants '
      + 'in the #cb-settings block in clock-builder.liquid.');
    alert('Checkout isn’t connected yet. Use “Save & share” to send us this '
      + 'build in the meantime.');
    return;
  }

  const { items, missing } = buildCartItems();

  if (missing.length) {
    // Refuse rather than add an incomplete order: a missing add-on means the
    // customer would be charged less than the quoted total.
    console.error('[clock-builder] Missing cart variant ids for:', missing.join(', '));
    alert('Something in this build isn’t available to order online just yet. '
      + 'Please use “Save & share” to send it to us and we’ll take it from there.');
    return;
  }

  const original = btn ? btn.textContent : '';
  setBtn('Adding…', true);

  try {
    // Record the build on the cart itself as well as the line item, so it
    // survives even if the customer removes and re-adds a line.
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Clock build': getSaveURL() } })
    }).catch(() => {});

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: items })
    });

    if (!res.ok) {
      // Shopify answers 422 with a human-readable description, which is usually
      // "sold out" or a bad variant id. Surface it rather than a generic error.
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.description || detail.message || ('HTTP ' + res.status));
    }

    window.location.href = CART.redirectTo || '/cart';
  } catch (e) {
    console.error('[clock-builder] Add to cart failed:', e);
    setBtn(original, false);
    alert('We couldn’t add this to your basket: ' + e.message
      + '\n\nYour design is safe — use “Save & share” to keep it.');
  }
}

// ─── Model selection ──────────────────────────────────────────────────────────

// Each size now sells itself rather than listing the same specification twice.
const modelFeatures = {
  weather:
    '\u2022 At 33cm / 13\u2033 diameter it is big enough to work as a single focal '
    + 'point in a room, small enough to sit alongside other wall art and furnishings.'
    + '<br><br>\u2022 Whether you want it to blend in or stand out, the Weather Clock is '
    + 'designed to suit a whole variety of settings and bring playfulness and charm '
    + 'wherever it is hung.',
  giant:
    '\u2022 At 53cm / 21\u2033 diameter, the Giant Weather Clock\u2019s giant rolling '
    + 'weather forecasts mesmerise everyone lucky enough to watch the mechanical animations.'
    + '<br><br>\u2022 Destined to become part of your family\u2019s folklore, it is at a scale '
    + 'large enough to fill a vacant wall and define a room.'
    + '<br><br>\u2022 An instant focal point whenever you entertain, this striking piece of '
    + 'kinetic art is handcrafted at our Hampshire workshop.'
};

document.querySelectorAll('.cb-model-card').forEach(card => {
  card.addEventListener('click', () => {
    document.querySelectorAll('.cb-model-card').forEach(c => c.classList.remove('active'));
    card.classList.add('active');
    config.model = card.dataset.model;
    const features = document.getElementById('cb-features-content');
    if (features) features.innerHTML = modelFeatures[config.model];
    updatePrice();
    swapModel(config.model);
    updateMaterialAvailability();
    updateFinishDescriptions();
    updateStationLabel();
    renderArtworkOptions();
  });
});

// The same finish is described differently per size: the Giant range is powder
// coated throughout, while on the Regular clock black, copper and chrome are
// anodised and only grey and blue are powder coated. Descriptions therefore
// cannot live in the markup — they are rewritten whenever the size changes.
const FINISH_DESCRIPTIONS = {
  giant: {
    'midnight-black': 'Powder coated',
    'summer-blue': 'Powder coated',
    'cloudy-grey': 'Powder coated'
  },
  weather: {
    'midnight-black': 'Anodised',
    'summer-blue': 'Powder coated',
    'cloudy-grey': 'Powder coated',
    copper: 'Anodised',
    aluminium: 'Anodised'
  }
};

function updateFinishDescriptions() {
  const forModel = FINISH_DESCRIPTIONS[config.model] || {};
  document.querySelectorAll('.cb-material-opt').forEach(opt => {
    const desc = opt.querySelector('.cb-material-opt__desc');
    if (!desc) return;
    const text = forModel[opt.dataset.material];
    // Custom RAL keeps whatever the markup says; it is the only option whose
    // description explains an interaction rather than a finish.
    if (text) desc.textContent = text;
  });
}

function updateMaterialAvailability() {
  document.querySelectorAll('.cb-material-opt[data-models]').forEach(opt => {
    const allowedModels = opt.dataset.models.split(',').map(m => m.trim());
    const available = allowedModels.includes(config.model);
    opt.style.display = available ? 'flex' : 'none';
    if (!available && config.material === opt.dataset.material) {
      const defaultOpt = document.querySelector('.cb-material-opt[data-material="midnight-black"]');
      if (defaultOpt) defaultOpt.click();
    }
  });
}

// ─── Material selection ───────────────────────────────────────────────────────

const PRESET_COLOURS = {
  'midnight-black': '#1a1a1a',
  'summer-blue': '#2a6096',
  'cloudy-grey': '#999999'
};

document.querySelectorAll('.cb-material-opt').forEach(opt => {
  opt.addEventListener('click', () => {
    document.querySelectorAll('.cb-material-opt').forEach(o => o.classList.remove('active'));
    opt.classList.add('active');
    config.material = opt.dataset.material;

    if (PRESET_COLOURS[config.material]) {
      setFramePowderWithColour(PRESET_COLOURS[config.material]);
    } else {
      setFrameMaterial(config.material);
      if (config.material === 'custom-ral' && config.ralColour) {
        setFramePowderWithColour(config.ralColour.hex);
      }
    }

    // The "available on the next step" notice is gone — the palette is on this
    // step now, so updateRALPaneVisibility() reveals it directly.
    updateRALPaneVisibility();
    updatePrice();
  });
});

// ─── RAL colours ─────────────────────────────────────────────────────────────

const RAL_COLOURS = [
  {code:'1000',name:'Green Beige',hex:'#cdc087',cat:'Yellow'},
  {code:'1001',name:'Beige',hex:'#d2b07a',cat:'Yellow'},
  {code:'1003',name:'Signal Yellow',hex:'#f7ba0b',cat:'Yellow'},
  {code:'1007',name:'Daffodil Yellow',hex:'#e0a000',cat:'Yellow'},
  {code:'1013',name:'Oyster White',hex:'#e7e0c9',cat:'Yellow'},
  {code:'1015',name:'Light Ivory',hex:'#e8dab6',cat:'Yellow'},
  {code:'1016',name:'Sulfur Yellow',hex:'#e7e22a',cat:'Yellow'},
  {code:'1017',name:'Saffron Yellow',hex:'#f6a950',cat:'Yellow'},
  {code:'1018',name:'Zinc Yellow',hex:'#ede93a',cat:'Yellow'},
  {code:'2001',name:'Red Orange',hex:'#be4e20',cat:'Orange'},
  {code:'2002',name:'Vermilion',hex:'#c63b20',cat:'Orange'},
  {code:'2003',name:'Pastel Orange',hex:'#fa7e25',cat:'Orange'},
  {code:'2004',name:'Pure Orange',hex:'#e25c00',cat:'Orange'},
  {code:'3000',name:'Flame Red',hex:'#9b2423',cat:'Red'},
  {code:'3001',name:'Signal Red',hex:'#8f1d1d',cat:'Red'},
  {code:'3002',name:'Carmine Red',hex:'#8d1d1d',cat:'Red'},
  {code:'3003',name:'Ruby Red',hex:'#7d1a1a',cat:'Red'},
  {code:'3004',name:'Purple Red',hex:'#6c1520',cat:'Red'},
  {code:'3005',name:'Wine Red',hex:'#4e1520',cat:'Red'},
  {code:'3012',name:'Beige Red',hex:'#c16950',cat:'Red'},
  {code:'3013',name:'Tomato Red',hex:'#9a2a22',cat:'Red'},
  {code:'3018',name:'Strawberry Red',hex:'#bf3040',cat:'Red'},
  {code:'3020',name:'Traffic Red',hex:'#bb2020',cat:'Red'},
  {code:'3027',name:'Raspberry Red',hex:'#9c1c35',cat:'Red'},
  {code:'4003',name:'Heather Violet',hex:'#c4708a',cat:'Pink'},
  {code:'4004',name:'Claret Violet',hex:'#5e1235',cat:'Pink'},
  {code:'4005',name:'Blue Lilac',hex:'#7a6a9a',cat:'Pink'},
  {code:'4006',name:'Traffic Purple',hex:'#831560',cat:'Pink'},
  {code:'4008',name:'Signal Violet',hex:'#8e3278',cat:'Pink'},
  {code:'5000',name:'Violet Blue',hex:'#2e4a6a',cat:'Blue'},
  {code:'5001',name:'Green Blue',hex:'#1b3c5a',cat:'Blue'},
  {code:'5002',name:'Ultramarine Blue',hex:'#1c2a7a',cat:'Blue'},
  {code:'5003',name:'Sapphire Blue',hex:'#1c2d4a',cat:'Blue'},
  {code:'5004',name:'Black Blue',hex:'#141928',cat:'Blue'},
  {code:'5005',name:'Signal Blue',hex:'#1a4080',cat:'Blue'},
  {code:'5007',name:'Brillant Blue',hex:'#3060a0',cat:'Blue'},
  {code:'5008',name:'Grey Blue',hex:'#2c4060',cat:'Blue'},
  {code:'5010',name:'Gentian Blue',hex:'#1a3060',cat:'Blue'},
  {code:'5011',name:'Steel Blue',hex:'#1a2050',cat:'Blue'},
  {code:'5012',name:'Light Blue',hex:'#3a80b0',cat:'Blue'},
  {code:'5013',name:'Cobalt Blue',hex:'#1c2d55',cat:'Blue'},
  {code:'5014',name:'Pigeon Blue',hex:'#6080a0',cat:'Blue'},
  {code:'5015',name:'Sky Blue',hex:'#2a70c0',cat:'Blue'},
  {code:'5017',name:'Traffic Blue',hex:'#0060a0',cat:'Blue'},
  {code:'5018',name:'Turquoise Blue',hex:'#2a8a80',cat:'Blue'},
  {code:'5019',name:'Capri Blue',hex:'#1a5070',cat:'Blue'},
  {code:'5020',name:'Ocean Blue',hex:'#103050',cat:'Blue'},
  {code:'5021',name:'Water Blue',hex:'#1a7080',cat:'Blue'},
  {code:'5022',name:'Night Blue',hex:'#1a1f50',cat:'Blue'},
  {code:'5023',name:'Distant Blue',hex:'#3a5a80',cat:'Blue'},
  {code:'5024',name:'Pastel Blue',hex:'#5a90b0',cat:'Blue'},
  {code:'6001',name:'Emerald Green',hex:'#2a6038',cat:'Green'},
  {code:'6002',name:'Leaf Green',hex:'#2a5830',cat:'Green'},
  {code:'6003',name:'Olive Green',hex:'#4a5030',cat:'Green'},
  {code:'6005',name:'Moss Green',hex:'#2a4028',cat:'Green'},
  {code:'6009',name:'Fir Green',hex:'#1e3028',cat:'Green'},
  {code:'6010',name:'Grass Green',hex:'#3c6030',cat:'Green'},
  {code:'6012',name:'Black Green',hex:'#2a3020',cat:'Green'},
  {code:'6016',name:'Turquoise Green',hex:'#1a7050',cat:'Green'},
  {code:'6017',name:'May Green',hex:'#4a7a30',cat:'Green'},
  {code:'6018',name:'Yellow Green',hex:'#5a8030',cat:'Green'},
  {code:'6019',name:'Pastel Green',hex:'#b0cc88',cat:'Green'},
  {code:'6021',name:'Pale Green',hex:'#7a9860',cat:'Green'},
  {code:'6024',name:'Traffic Green',hex:'#2a8050',cat:'Green'},
  {code:'6027',name:'Light Green',hex:'#78c8a0',cat:'Green'},
  {code:'6028',name:'Pine Green',hex:'#2a5038',cat:'Green'},
  {code:'6032',name:'Signal Green',hex:'#2a8048',cat:'Green'},
  {code:'6033',name:'Mint Turquoise',hex:'#4a9880',cat:'Green'},
  {code:'6034',name:'Pastel Turquoise',hex:'#7ac0b0',cat:'Green'},
  {code:'7001',name:'Silver Grey',hex:'#8a9298',cat:'Grey'},
  {code:'7004',name:'Signal Grey',hex:'#9a9898',cat:'Grey'},
  {code:'7005',name:'Mouse Grey',hex:'#6e7370',cat:'Grey'},
  {code:'7009',name:'Green Grey',hex:'#5a6055',cat:'Grey'},
  {code:'7011',name:'Iron Grey',hex:'#505558',cat:'Grey'},
  {code:'7012',name:'Basalt Grey',hex:'#4c5458',cat:'Grey'},
  {code:'7015',name:'Slate Grey',hex:'#404850',cat:'Grey'},
  {code:'7016',name:'Anthracite Grey',hex:'#383e42',cat:'Grey'},
  {code:'7021',name:'Black Grey',hex:'#2f3234',cat:'Grey'},
  {code:'7022',name:'Umbra Grey',hex:'#3e3e38',cat:'Grey'},
  {code:'7024',name:'Graphite Grey',hex:'#454c50',cat:'Grey'},
  {code:'7026',name:'Granite Grey',hex:'#3a4245',cat:'Grey'},
  {code:'7030',name:'Stone Grey',hex:'#8e8c80',cat:'Grey'},
  {code:'7031',name:'Blue Grey',hex:'#5a6870',cat:'Grey'},
  {code:'7032',name:'Pebble Grey',hex:'#b8b2a0',cat:'Grey'},
  {code:'7035',name:'Light Grey',hex:'#c8c8c0',cat:'Grey'},
  {code:'7037',name:'Dusty Grey',hex:'#7a7878',cat:'Grey'},
  {code:'7038',name:'Agate Grey',hex:'#b8b4aa',cat:'Grey'},
  {code:'7039',name:'Quartz Grey',hex:'#6a6868',cat:'Grey'},
  {code:'7042',name:'Traffic Grey A',hex:'#8e9298',cat:'Grey'},
  {code:'7043',name:'Traffic Grey B',hex:'#4a5055',cat:'Grey'},
  {code:'7044',name:'Silk Grey',hex:'#b0aca0',cat:'Grey'},
  {code:'7046',name:'Telegrey 2',hex:'#7a8288',cat:'Grey'},
  {code:'7047',name:'Telegrey 4',hex:'#c8c8c8',cat:'Grey'},
  {code:'8002',name:'Signal Brown',hex:'#6a3a2a',cat:'Brown'},
  {code:'8003',name:'Clay Brown',hex:'#7a4a20',cat:'Brown'},
  {code:'8008',name:'Olive Brown',hex:'#6a4818',cat:'Brown'},
  {code:'8011',name:'Nut Brown',hex:'#4a2810',cat:'Brown'},
  {code:'8014',name:'Sepia Brown',hex:'#3e2510',cat:'Brown'},
  {code:'8019',name:'Grey Brown',hex:'#3a3028',cat:'Brown'},
  {code:'8022',name:'Black Brown',hex:'#1a1210',cat:'Brown'},
  {code:'9001',name:'Cream',hex:'#f0ead8',cat:'White & Black'},
  {code:'9002',name:'Grey White',hex:'#e0dcd0',cat:'White & Black'},
  {code:'9003',name:'Signal White',hex:'#f2f0e8',cat:'White & Black'},
  {code:'9004',name:'Signal Black',hex:'#28282a',cat:'White & Black'},
  {code:'9005',name:'Jet Black',hex:'#0e0e10',cat:'White & Black'},
  {code:'9006',name:'White Aluminium',hex:'#a0a0a0',cat:'White & Black'},
  {code:'9007',name:'Grey Aluminium',hex:'#808080',cat:'White & Black'},
  {code:'9010',name:'Pure White',hex:'#f8f4ec',cat:'White & Black'},
  {code:'9011',name:'Graphite Black',hex:'#1c1c1e',cat:'White & Black'},
  {code:'9016',name:'Traffic White',hex:'#f2f0e8',cat:'White & Black'},
  {code:'9017',name:'Traffic Black',hex:'#1e1e1e',cat:'White & Black'},
  {code:'9018',name:'Papyrus White',hex:'#d8d4c8',cat:'White & Black'},
];

const RAL_CATS = [...new Set(RAL_COLOURS.map(c => c.cat))];
let activeRalCat = 'Grey';
let activeRalColour = null;

function initRAL() {
  const catsEl = document.getElementById('cb-ral-cats');
  const gridEl = document.getElementById('cb-ral-grid');
  if (!catsEl || !gridEl) { console.error('RAL elements not found'); return; }

  RAL_CATS.forEach(cat => {
    const btn = document.createElement('button');
    btn.className = 'cb-ral-cat' + (cat === activeRalCat ? ' active' : '');
    btn.textContent = cat;
    btn.addEventListener('click', () => {
      activeRalCat = cat;
      document.querySelectorAll('.cb-ral-cat').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById('cb-ral-search').value = '';
      renderRALGrid();
    });
    catsEl.appendChild(btn);
  });

  document.getElementById('cb-ral-search').addEventListener('input', () => {
    activeRalCat = '';
    document.querySelectorAll('.cb-ral-cat').forEach(b => b.classList.remove('active'));
    renderRALGrid();
  });

  renderRALGrid();

  // Seed the RAL swatch without switching the frame to Custom RAL — otherwise
  // the builder opens on 'custom-ral' and the starting price includes +£250.
  const defaultColour = RAL_COLOURS.find(c => c.code === '9005');
  if (defaultColour) selectRALColour(defaultColour, { silent: true });
}

function renderRALGrid() {
  const query = document.getElementById('cb-ral-search').value.toLowerCase();
  const grid = document.getElementById('cb-ral-grid');
  grid.innerHTML = '';

  const filtered = RAL_COLOURS.filter(c => {
    const matchCat = !activeRalCat || c.cat === activeRalCat;
    const matchQuery = !query || c.code.includes(query) || c.name.toLowerCase().includes(query);
    return matchCat && matchQuery;
  });

  filtered.forEach(c => {
    const swatch = document.createElement('div');
    swatch.className = 'cb-ral-swatch' + (activeRalColour && activeRalColour.code === c.code ? ' selected' : '');
    swatch.style.background = c.hex;
    swatch.title = `RAL ${c.code} — ${c.name}`;
    swatch.addEventListener('click', () => selectRALColour(c));
    grid.appendChild(swatch);
  });
}

// silent: true seeds the picker's state without selecting the Custom RAL
// finish — used at boot so the default stays Midnight Black.
function selectRALColour(c, { silent = false } = {}) {
  activeRalColour = c;
  config.ralColour = c;

  if (!silent) {
    setFramePowderWithColour(c.hex);

    document.querySelectorAll('.cb-material-opt').forEach(o => o.classList.remove('active'));
    const customRalOpt = document.querySelector('.cb-material-opt[data-material="custom-ral"]');
    if (customRalOpt) customRalOpt.classList.add('active');
    config.material = 'custom-ral';
  }

  document.getElementById('cb-ral-dot').style.background = c.hex;
  document.getElementById('cb-ral-name').textContent = `RAL ${c.code} — ${c.name}`;
  document.getElementById('cb-ral-code').textContent = 'Currently selected';

  document.querySelectorAll('.cb-ral-swatch').forEach(s => {
    s.classList.toggle('selected', s.title === `RAL ${c.code} — ${c.name}`);
  });

  if (!silent) updatePrice();
}

// The palette now lives on the same step as the finishes, so it is simply shown
// for Custom RAL and hidden for everything else. The old "not available"
// placeholder and its "change frame finish" button are gone with the merge.
function updateRALPaneVisibility() {
  const picker = document.getElementById('cb-ral-picker');
  if (picker) picker.style.display = config.material === 'custom-ral' ? '' : 'none';
}

// ─── Face text ────────────────────────────────────────────────────────────────

// Opt-in toggle, mirroring the plaque: it charges and reveals the fields in one
// place, so the price can never move while the customer is only typing.
const faceTextToggleRow = document.getElementById('cb-facetext-toggle-row');
const faceTextTrack = document.querySelector('#cb-facetext-toggle .cb-toggle__track');
const faceTextFields = document.getElementById('cb-facetext-fields');

function applyFaceTextState() {
  if (faceTextTrack) faceTextTrack.classList.toggle('on', faceTextOn());
  if (faceTextToggleRow) faceTextToggleRow.classList.toggle('active', faceTextOn());
  if (faceTextFields) faceTextFields.style.display = faceTextOn() ? 'block' : 'none';
  updateFaceTextPriceLabel();
}

// The charge is shown on the toggle, but a commission already covers the face,
// so the same control has to read "Included" there.
function updateFaceTextPriceLabel() {
  const el = document.getElementById('cb-facetext-price');
  if (!el) return;
  if (isFullCustom()) { el.textContent = 'Included'; return; }
  const price = faceTextPrice();
  el.textContent = price > 0 ? '+' + formatMoney(price) : 'Included';
}

if (faceTextToggleRow) {
  faceTextToggleRow.addEventListener('click', () => {
    config.faceTextOn = !faceTextOn();
    applyFaceTextState();
    updateFaceTexture();
    updatePrice();
  });
}

document.getElementById('cb-face-sub').addEventListener('input', (e) => {
  config.faceSub = e.target.value;
  updateFaceTexture();
});

document.getElementById('cb-weather-label').addEventListener('input', (e) => {
  config.weatherLabel = e.target.value;
  updateFaceTexture();
});

// Logos, crests and extra lines are quoted by hand, so this is free text that
// travels with the order rather than a priced option.
const faceExtrasEl = document.getElementById('cb-face-extras');
if (faceExtrasEl) {
  faceExtrasEl.addEventListener('input', (e) => {
    config.faceExtras = e.target.value;
  });
}

document.querySelectorAll('.cb-font-opt').forEach(opt => {
  opt.addEventListener('click', () => {
    document.querySelectorAll('.cb-font-opt').forEach(o => o.classList.remove('active'));
    opt.classList.add('active');
    config.font = opt.dataset.font;
    updateFaceTexture();
  });
});

document.querySelectorAll('.cb-colour-swatch').forEach(swatch => {
  swatch.addEventListener('click', () => {
    document.querySelectorAll('.cb-colour-swatch').forEach(s => s.classList.remove('active'));
    swatch.classList.add('active');
    config.textColour = swatch.dataset.colour;
    updateFaceTexture();
  });
});

function updateStationLabel() {
  const el = document.getElementById('cb-station-label-title');
  if (el) el.textContent = config.model === 'weather' ? 'Lower line message' : 'Lower line message';
}

updateStationLabel();

// ─── Face text canvas texture ─────────────────────────────────────────────────

let faceCanvas = null;
let faceTexture = null;
let faceBaseImage = null;

const FONT_MAP = {
  mono: '500 {size}px "Lucida Console", "Consolas", "Menlo", monospace',
  serif: 'italic {size}px Georgia, serif',
  sans: '700 {size}px Arial, sans-serif',
  light: '300 {size}px Arial, sans-serif'
};

function initFaceCanvas() {
  if (!meshes.face) return;

  faceCanvas = document.createElement('canvas');
  faceCanvas.width = 1024;
  faceCanvas.height = 1024;

  const existingMap = meshes.face.material.map;
  if (existingMap && existingMap.image) {
    faceBaseImage = existingMap.image;
    if (DEBUG) console.log('Face base texture found:', faceBaseImage.width, faceBaseImage.height);
  } else if (DEBUG) {
    console.log('No existing face texture — using flat background');
  }

  faceTexture = new THREE.CanvasTexture(faceCanvas);
  faceTexture.colorSpace = THREE.SRGBColorSpace;
  faceTexture.flipY = false;
  meshes.face.material.map = faceTexture;
  meshes.face.material.needsUpdate = true;

  updateFaceTexture();
}

function updateFaceTexture() {
  if (!faceCanvas || !faceTexture) return;

  const ctx = faceCanvas.getContext('2d');
  const size = 1024;
  const cx = size / 2;
  const cy = size / 2;

  ctx.clearRect(0, 0, size, size);

  if (faceBaseImage) {
    ctx.drawImage(faceBaseImage, 0, 0, size, size);
  } else {
    ctx.fillStyle = '#f0f0ec';
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  const font = (px) => (FONT_MAP[config.font] || FONT_MAP.mono).replace('{size}', px);
  ctx.textAlign = 'center';

  const ft = modelSettings().faceText;

  // Nothing is drawn while the option is off, so switching it back off clears
  // the dial rather than leaving the last text on it.
  if (!faceTextOn()) {
    faceTexture.needsUpdate = true;
    return;
  }

  // Order matters: assigning ctx.font resets letterSpacing to 0, so the
  // spacing must be set AFTER the font on every field. Both lines are drawn
  // exactly as typed — they used to be upper-cased here, which overrode
  // whatever case the customer chose.
  if (config.faceSub.trim()) {
    ctx.font = font(ft.subSize);
    ctx.letterSpacing = (ft.subTrack ?? 4) + 'px';
    ctx.fillStyle = config.textColour;
    ctx.fillText(config.faceSub, cx, cy + ft.subY);
  }

  if (config.weatherLabel.trim()) {
    ctx.font = font(ft.labelSize);
    ctx.letterSpacing = (ft.labelTrack ?? 4) + 'px';
    ctx.fillStyle = config.textColour;
    // As typed, same as the title above.
    ctx.fillText(config.weatherLabel, cx, cy + ft.labelY);
  }

  ctx.letterSpacing = '0px';

  faceTexture.needsUpdate = true;
}

// ─── Plaque ───────────────────────────────────────────────────────────────────

const plaqueToggleRow = document.getElementById('cb-plaque-toggle-row');
const plaqueTrack = document.querySelector('#cb-plaque-toggle .cb-toggle__track');
const plaqueFields = document.getElementById('cb-plaque-fields');

plaqueToggleRow.addEventListener('click', () => {
  config.plaque = !config.plaque;
  plaqueTrack.classList.toggle('on', config.plaque);
  plaqueToggleRow.classList.toggle('active', config.plaque);
  plaqueFields.style.display = config.plaque ? 'block' : 'none';

  if (config.plaque) {
    framePlaque();
    document.querySelectorAll('.cb-view-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.view === 'back');
    });
  } else {
    setView('front');
    document.querySelectorAll('.cb-view-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.view === 'front');
    });
  }

  updatePrice();
  updatePlaqueTexture();
});

document.querySelectorAll('.cb-plaque-type').forEach(type => {
  type.addEventListener('click', () => {
    document.querySelectorAll('.cb-plaque-type').forEach(t => t.classList.remove('active'));
    type.classList.add('active');
    config.plaqueType = type.dataset.type;
    document.getElementById('cb-plaque-typeface').style.display = config.plaqueType === 'typeface' ? 'block' : 'none';
    document.getElementById('cb-plaque-handwritten').style.display = config.plaqueType === 'handwritten' ? 'block' : 'none';
    updatePlaqueTexture();
  });
});

document.getElementById('cb-plaque-message').addEventListener('input', (e) => {
  const lines = e.target.value.split('\n');
  if (lines.length > 4) {
    e.target.value = lines.slice(0, 4).join('\n');
  }
  config.plaqueMessage = e.target.value;

  const count = e.target.value.length;
  const counter = document.getElementById('cb-plaque-char-count');
  if (counter) {
    counter.textContent = count + ' / 120';
    counter.classList.toggle('over', count >= 120);
  }

  updatePlaqueTexture();
});

document.querySelectorAll('.cb-align-opt').forEach(opt => {
  opt.addEventListener('click', () => {
    document.querySelectorAll('.cb-align-opt').forEach(o => o.classList.remove('active'));
    opt.classList.add('active');
    config.plaqueAlign = opt.dataset.align;
    updatePlaqueTexture();
  });
});

document.querySelectorAll('#cb-plaque-font-grid .cb-font-opt').forEach(opt => {
  opt.addEventListener('click', () => {
    document.querySelectorAll('#cb-plaque-font-grid .cb-font-opt').forEach(o => o.classList.remove('active'));
    opt.classList.add('active');
    config.plaqueFont = opt.dataset.font;
    updatePlaqueTexture();
  });
});

const uploadZone = document.getElementById('cb-plaque-upload-zone');
const uploadInput = document.getElementById('cb-plaque-upload');
const uploadPreview = document.getElementById('cb-upload-preview');
const uploadImg = document.getElementById('cb-upload-img');
const uploadClear = document.getElementById('cb-upload-clear');

uploadZone.addEventListener('click', (e) => {
  e.stopPropagation();
  uploadInput.click();
});

uploadZone.addEventListener('dragover', (e) => {
  e.preventDefault();
  uploadZone.style.borderColor = '#1a1a1a';
});

uploadZone.addEventListener('dragleave', () => {
  uploadZone.style.borderColor = '#ccc';
});

uploadZone.addEventListener('drop', (e) => {
  e.preventDefault();
  uploadZone.style.borderColor = '#ccc';
  const file = e.dataTransfer.files[0];
  if (file) handlePlaqueUpload(file);
});

uploadInput.addEventListener('change', (e) => {
  if (e.target.files[0]) handlePlaqueUpload(e.target.files[0]);
});

uploadClear.addEventListener('click', () => {
  config.plaqueImage = null;
  config.plaqueImageEl = null;
  uploadImg.src = '';
  uploadPreview.style.display = 'none';
  uploadZone.style.display = 'block';
  updatePlaqueTexture();
});

function handlePlaqueUpload(file) {
  const allowed = ['image/jpeg', 'image/png'];
  if (!allowed.includes(file.type)) {
    alert('Please upload a JPG or PNG file. iPhone photos should be exported as JPG — open the image in Photos, tap Share, then Save as JPG.');
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    alert('File is too large. Please upload an image under 10MB.');
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    config.plaqueImage = e.target.result;
    uploadImg.src = e.target.result;
    uploadPreview.style.display = 'block';
    uploadZone.style.display = 'none';
    const img = new Image();
    img.onload = () => {
      config.plaqueImageEl = img;
      updatePlaqueTexture();
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
}

// ─── Plaque canvas texture ────────────────────────────────────────────────────

let plaqueCanvas = null;
let plaqueTexture = null;
let plaqueBaseZ = null;

// The plaque is a flat plane sitting on the clock's back panel. On some models
// it is coplanar with that panel, which makes it z-fight or disappear entirely.
// Nudge it outward along -Z by a small per-model amount.
function applyPlaqueOffset() {
  const p = meshes.plaque;
  if (!p) return;
  if (plaqueBaseZ === null) plaqueBaseZ = p.position.z;
  p.position.z = plaqueBaseZ - (modelSettings().plaqueOffset || 0);
}

function initPlaqueCanvas() {
  if (!meshes.plaque) {
    if (DEBUG) console.log('No plaque mesh found — skipping plaque canvas');
    return;
  }

  plaqueCanvas = document.createElement('canvas');
  plaqueCanvas.width = 512;
  plaqueCanvas.height = 320;

  plaqueTexture = new THREE.CanvasTexture(plaqueCanvas);
  plaqueTexture.colorSpace = THREE.SRGBColorSpace;
  plaqueTexture.flipY = true;

  meshes.plaque.material = new THREE.MeshStandardMaterial({
    map: plaqueTexture,
    emissiveMap: plaqueTexture,
    emissive: new THREE.Color(0x333333),
    roughness: 0.4,
    metalness: 0.6,
    // The plaque is a flat, zero-depth plane. Its normal direction differs
    // between models, so render both faces rather than relying on it.
    side: THREE.DoubleSide,
    // Treat it as a decal: bias its depth so it wins against the coplanar
    // back panel without needing to be physically moved.
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  meshes.plaque.material.needsUpdate = true;
  meshes.plaque.visible = false;
  applyPlaqueOffset();
  updatePlaqueTexture();
}

// The plaque texture needs orienting to match the mesh's UVs, which differ
// per model. 'rotate180' suits a plane whose normal faces out of the back;
// 'mirror' suits one facing the opposite way; 'none' leaves it untouched.
function applyPlaqueTransform(ctx, w, h) {
  const mode = modelSettings().plaqueTransform || 'rotate180';
  if (mode === 'rotate180') {
    ctx.translate(w, h);
    ctx.scale(-1, -1);
  } else if (mode === 'mirror') {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
  }
}

function updatePlaqueTexture() {
  if (!plaqueCanvas || !plaqueTexture) return;

  const ctx = plaqueCanvas.getContext('2d');
  const w = 512;
  const h = 320;
  const cx = w / 2;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#e8e4d8';
  ctx.fillRect(0, 0, w, h);

  if (!config.plaque) {
    if (meshes.plaque) meshes.plaque.visible = false;
    plaqueTexture.needsUpdate = true;
    return;
  }

  if (meshes.plaque) meshes.plaque.visible = true;

  const fontKey = config.plaqueFont || 'mono';
  const font = (px) => (FONT_MAP[fontKey] || FONT_MAP.mono).replace('{size}', px);

  if (config.plaqueType === 'handwritten' && config.plaqueImageEl && config.plaqueImageEl.complete) {
    ctx.save();
    applyPlaqueTransform(ctx, w, h);
    ctx.fillRect(20, 20, w - 40, h - 40);
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(config.plaqueImageEl, 20, 20, w - 40, h - 40);
    ctx.restore();
    plaqueTexture.needsUpdate = true;
    return;
  }

  const maxWidth = w - 80;
  const maxLines = 4;

  function wrapLine(ctx, text, maxWidth) {
    const words = text.split(' ');
    const wrapped = [];
    let current = '';
    words.forEach(word => {
      if (ctx.measureText(word).width > maxWidth) {
        if (current) { wrapped.push(current); current = ''; }
        let chars = '';
        for (const char of word) {
          if (ctx.measureText(chars + char).width > maxWidth) {
            wrapped.push(chars);
            chars = char;
          } else {
            chars += char;
          }
        }
        if (chars) { current = chars; }
        return;
      }
      const test = current ? current + ' ' + word : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        wrapped.push(current);
        current = word;
      } else {
        current = test;
      }
    });
    if (current) wrapped.push(current);
    return wrapped;
  }

  const inputLines = config.plaqueMessage ? config.plaqueMessage.split('\n').filter(l => l.trim()) : [];
  if (inputLines.length === 0) {
    plaqueTexture.needsUpdate = true;
    return;
  }

  const baseFontSize = 26;
  const allWrapped = [];

  ctx.save();
  inputLines.forEach((line, i) => {
    if (allWrapped.length >= maxLines) return;
    const fontSize = i === 0 ? baseFontSize : Math.floor(baseFontSize * 0.85);
    ctx.font = font(fontSize);
    const wrapped = wrapLine(ctx, line, maxWidth);
    wrapped.forEach(w => {
      if (allWrapped.length < maxLines) {
        allWrapped.push({ text: w, isFirst: i === 0 });
      }
    });
  });
  ctx.restore();

  const totalLines = allWrapped.length;
  const lineHeight = baseFontSize * 1.6;
  const totalTextHeight = totalLines * lineHeight;
  const startY = (h - totalTextHeight) / 2 + lineHeight * 0.8;

  ctx.save();
  applyPlaqueTransform(ctx, w, h);

  const alignX = config.plaqueAlign === 'left' ? 40 : config.plaqueAlign === 'right' ? w - 40 : cx;
  ctx.textAlign = config.plaqueAlign === 'center' ? 'center' : config.plaqueAlign === 'right' ? 'right' : 'left';

  allWrapped.forEach((item, i) => {
    const fontSize = item.isFirst ? baseFontSize : Math.floor(baseFontSize * 0.85);
    ctx.font = font(fontSize);
    ctx.fillStyle = item.isFirst ? '#1a1a1a' : '#444';
    ctx.fillText(item.text, alignX, startY + lineHeight * i);
  });

  ctx.restore();
  plaqueTexture.needsUpdate = true;
}

// ─── Preview shots ────────────────────────────────────────────────────────────

const SHOT_POSITIONS = {
  'front':       { pos: new THREE.Vector3(0, 0, 3),           target: new THREE.Vector3(0, 0, 0) },
  'angle-left':  { pos: new THREE.Vector3(-2.2, 0.4, 1.8),    target: new THREE.Vector3(0, 0, 0) },
  'angle-right': { pos: new THREE.Vector3(2.2, 0.4, 1.8),     target: new THREE.Vector3(0, 0, 0) },
  'back':        { pos: new THREE.Vector3(1.26, -0.56, -1.4), target: new THREE.Vector3(0.28, -0.35, 0.14) }
};

function initPreviewShots() {
  const backShot = document.getElementById('cb-shot-back');
  if (backShot) backShot.style.display = config.plaque ? 'block' : 'none';

  document.querySelectorAll('.cb-shot').forEach(shot => {
    shot.addEventListener('click', () => {
      document.querySelectorAll('.cb-shot').forEach(s => s.classList.remove('active'));
      shot.classList.add('active');
      const key = shot.dataset.shot;
      if (key === 'back') {
        framePlaque();
        return;
      }
      const position = SHOT_POSITIONS[key];
      if (position) animateCameraTo(position.pos, position.target);
    });
  });
}

initPreviewShots();

// ─── Price breakdown ──────────────────────────────────────────────────────────

function buildPriceBreakdown() {
  const el = document.getElementById('cb-price-breakdown');
  if (!el) return;

  const matNames = {
    'midnight-black': 'Midnight Black',
    'summer-blue': 'Summer Blue',
    'cloudy-grey': 'Cloudy Grey',
    'custom-ral': config.ralColour ? `Custom RAL ${config.ralColour.code}` : 'Custom RAL',
    copper: 'Brushed Copper',
    aluminium: 'Chrome'
  };

  const modelNames = { weather: 'Weather Clock', giant: 'Giant Weather Clock' };
  const artwork = (ARTWORK[config.model] || {})[config.artwork];
  const full = isFullCustom();
  const money = formatMoney;

  const inc = 'Included';

  // A commission is one price covering the clock, the bespoke face and the
  // bespoke artwork. Those are still listed individually so the customer and
  // the workshop can see the full specification. The frame and the plaque are
  // charged on top exactly as they are on a standard build.
  const rows = [
    {
      label: full
        ? (modelNames[config.model] || 'Clock') + ' — full custom commission'
        : (modelNames[config.model] || 'Clock'),
      val: money(full ? PRICES.fullCustom[config.model] : PRICES.model[config.model])
    },
    full && artwork ? {
      label: '  · Bespoke artwork — ' + artwork.name,
      val: inc, included: true
    } : artwork ? {
      label: 'Artwork — ' + artwork.name,
      val: inc, included: true
    } : null,
    full ? {
      label: '  · Bespoke clock face',
      val: inc, included: true
    } : (faceTextOn() ? {
      label: 'Face text & labels',
      val: '+' + money(faceTextPrice())
    } : null),
    PRICES.material[config.material] > 0 ? {
      label: matNames[config.material],
      val: '+' + money(PRICES.material[config.material])
    } : {
      label: matNames[config.material],
      val: inc, included: true
    },
    config.plaque ? {
      label: 'Personalised plaque',
      val: '+' + money(PRICES.plaque)
    } : null,
    { label: 'Total', val: money(calcTotal()), total: true }
  ].filter(Boolean);

  // Commissions are taken as a deposit plus a balance on completion.
  if (full) {
    rows.push(
      {
        label: depositIsFixed()
          ? 'Deposit payable today'
          : 'Deposit payable today (' + DEPOSIT_PERCENT + '%)',
        val: money(depositAmount()), terms: true
      },
      { label: 'Balance due on completion', val: money(balanceAmount()), terms: true }
    );
  }

  el.innerHTML = rows.map(r => `
    <div class="cb-pb-row${r.total ? ' cb-pb-row--total' : ''}${r.included ? ' cb-pb-row--included' : ''}${r.terms ? ' cb-pb-row--terms' : ''}">
      <span class="cb-pb-row__label">${r.label}</span>
      <span class="cb-pb-row__val">${r.val}</span>
    </div>
  `).join('');
}

// Every configured choice, for the cart line item properties and the order.
// Under a full-custom commission each option still appears, priced at zero,
// so the order records the full specification.
function buildLineItemProperties() {
  const matNames = {
    'midnight-black': 'Midnight Black',
    'summer-blue': 'Summer Blue',
    'cloudy-grey': 'Cloudy Grey',
    'custom-ral': config.ralColour ? `RAL ${config.ralColour.code} — ${config.ralColour.name}` : 'Custom RAL',
    copper: 'Brushed Copper',
    aluminium: 'Chrome'
  };
  const modelNames = { weather: 'Weather Clock', giant: 'Giant Weather Clock' };
  const artwork = (ARTWORK[config.model] || {})[config.artwork];
  const full = isFullCustom();
  const props = {};

  // Shopify already shows the product title and the variant's own option, so
  // repeating them as properties just pads the line. They are still needed on
  // the deposit line, which has no clock variant behind it to say them.
  //
  // commissionMode() is the shop's setting for how commissions are sold, NOT
  // whether this particular line is a deposit — only a full custom build in
  // deposit mode produces one. Testing the setting alone suppressed nothing,
  // because the setting is 'deposit' for every build.
  const isDepositLine = isFullCustom() && commissionMode() === 'deposit';
  const variantSaysIt = !isDepositLine && materialIsVariant();

  if (!variantSaysIt) props['Model'] = modelNames[config.model] || config.model;
  if (artwork) props['Artwork'] = artwork.name;
  if (full) {
    props['Commission'] = 'Full custom \u2014 bespoke face and bespoke artwork';
    props['Payment terms'] = depositIsFixed()
      ? formatMoney(depositAmount()) + ' on order, balance on completion'
      : DEPOSIT_PERCENT + '% deposit on order, '
        + (100 - DEPOSIT_PERCENT) + '% balance on completion';
    // In deposit mode the line's own price is the deposit, and buildCartItems
    // adds the order total and the balance — so stating them here as well just
    // repeats the same figures back at the customer.
    if (commissionMode() !== 'deposit') {
      props['Deposit'] = formatMoney(depositAmount());
      props['Balance on completion'] = formatMoney(balanceAmount());
    }
  }
  // Keep the finish for custom RAL even when it is a variant: the variant is
  // just called "Custom RAL colour" and the actual colour is only here.
  if (!variantSaysIt || config.material === 'custom-ral') {
    props['Frame finish'] = matNames[config.material] || config.material;
  }

  if (faceTextOn()) {
    if (config.faceSub.trim()) props['Top line'] = config.faceSub;
    if (config.weatherLabel.trim()) props['Bottom line'] = config.weatherLabel;
    if (config.faceSub.trim() || config.weatherLabel.trim()) {
      props['Face font'] = config.font;
      props['Face text colour'] = config.textColour;
    }

    // Quoted by hand, so it is flagged rather than priced. It sits inside the
    // toggle now, so it follows the same rule as the two lines: if the option
    // is off the customer cannot see the field, and nothing from it is sent.
    if (config.faceExtras.trim()) props['Face additions (to be quoted)'] = config.faceExtras;
  }

  if (config.plaque) {
    props['Plaque'] = config.plaqueType === 'handwritten' ? 'Handwritten (image supplied)' : 'Typeface';
    if (config.plaqueType === 'typeface') {
      props['Plaque message'] = config.plaqueMessage;
      props['Plaque alignment'] = config.plaqueAlign;
      props['Plaque font'] = config.plaqueFont;
    }
  }

  if (config.additionalNotes.trim()) props['Additional instructions'] = config.additionalNotes;

  return props;
}

const notesEl = document.getElementById('cb-additional-notes');
if (notesEl) {
  notesEl.addEventListener('input', (e) => {
    config.additionalNotes = e.target.value;
  });
}

// ─── Save & Share ─────────────────────────────────────────────────────────────

function serialiseConfig() {
  const data = {
    m: config.model,
    aw: config.artwork,
    mat: config.material,
    ral: config.ralColour ? config.ralColour.code : null,
    fo: config.faceTextOn,
    fs: config.faceSub,
    wl: config.weatherLabel,
    fx: config.faceExtras,
    tc: config.textColour,
    fn: config.font,
    pl: config.plaque,
    pt: config.plaqueType,
    pm: config.plaqueMessage,
    pa: config.plaqueAlign,
    pf: config.plaqueFont,
    an: config.additionalNotes
  };
  return btoa(JSON.stringify(data));
}

function deserialiseConfig(encoded) {
  try {
    const data = JSON.parse(atob(encoded));
    if (data.m) config.model = data.m;
    if (data.aw) config.artwork = data.aw;
    if (data.mat) config.material = data.mat;
    if (data.ral) {
      const ralColour = RAL_COLOURS.find(c => c.code === data.ral);
      if (ralColour) config.ralColour = ralColour;
    }
    // `ft` was the clock title, removed at the client's request. Links saved
    // before that still carry it; it is ignored rather than treated as an error.
    if (typeof data.fo === 'boolean') config.faceTextOn = data.fo;
    if (data.fs) config.faceSub = data.fs;
    if (data.wl) config.weatherLabel = data.wl;
    if (data.fx) config.faceExtras = data.fx;
    // An older link had no toggle, so infer it from whichever lines it carried.
    if (typeof data.fo !== 'boolean' && (data.fs || data.wl)) config.faceTextOn = true;
    if (data.tc) config.textColour = data.tc;
    if (data.fn) config.font = data.fn;
    if (data.pl) config.plaque = data.pl;
    if (data.pt) config.plaqueType = data.pt;
    if (data.pm) config.plaqueMessage = data.pm;
    if (data.pa) config.plaqueAlign = data.pa;
    if (data.pf) config.plaqueFont = data.pf;
    if (data.an) config.additionalNotes = data.an;
    return true;
  } catch (e) {
    console.error('Failed to deserialise config:', e);
    return false;
  }
}

function getSaveURL() {
  const encoded = serialiseConfig();
  const url = new URL(window.location.href);
  url.searchParams.set('config', encoded);
  return url.toString();
}

function applyDeserialisedConfig() {
  document.querySelectorAll('.cb-model-card').forEach(c => {
    c.classList.toggle('active', c.dataset.model === config.model);
  });

  document.querySelectorAll('.cb-material-opt').forEach(o => {
    o.classList.toggle('active', o.dataset.material === config.material);
  });
  if (PRESET_COLOURS[config.material]) {
    setFramePowderWithColour(PRESET_COLOURS[config.material]);
  } else if (config.material === 'custom-ral' && config.ralColour) {
    setFramePowderWithColour(config.ralColour.hex);
  } else {
    setFrameMaterial(config.material);
  }

  if (config.ralColour) {
    activeRalColour = config.ralColour;
    const dot = document.getElementById('cb-ral-dot');
    const name = document.getElementById('cb-ral-name');
    const code = document.getElementById('cb-ral-code');
    if (dot) dot.style.background = config.ralColour.hex;
    if (name) name.textContent = `RAL ${config.ralColour.code} — ${config.ralColour.name}`;
    if (code) code.textContent = 'Currently selected';
  }

  const subEl = document.getElementById('cb-face-sub');
  const weatherEl = document.getElementById('cb-weather-label');
  const extrasEl = document.getElementById('cb-face-extras');
  if (subEl) subEl.value = config.faceSub;
  if (weatherEl) weatherEl.value = config.weatherLabel;
  if (extrasEl) extrasEl.value = config.faceExtras;

  // Reflect the restored toggle, which also decides whether the fields show.
  applyFaceTextState();

  document.querySelectorAll('.cb-font-opt').forEach(o => {
    o.classList.toggle('active', o.dataset.font === config.font);
  });

  document.querySelectorAll('.cb-colour-swatch').forEach(s => {
    s.classList.toggle('active', s.dataset.colour === config.textColour);
  });

  const plaqueTrackEl = document.querySelector('#cb-plaque-toggle .cb-toggle__track');
  const plaqueFieldsEl = document.getElementById('cb-plaque-fields');
  const plaqueToggleRowEl = document.getElementById('cb-plaque-toggle-row');
  if (plaqueTrackEl) plaqueTrackEl.classList.toggle('on', config.plaque);
  if (plaqueToggleRowEl) plaqueToggleRowEl.classList.toggle('active', config.plaque);
  if (plaqueFieldsEl) plaqueFieldsEl.style.display = config.plaque ? 'block' : 'none';

  if (config.plaqueType) {
    document.querySelectorAll('.cb-plaque-type').forEach(t => {
      t.classList.toggle('active', t.dataset.type === config.plaqueType);
    });
    const typefaceEl = document.getElementById('cb-plaque-typeface');
    const handwrittenEl = document.getElementById('cb-plaque-handwritten');
    if (typefaceEl) typefaceEl.style.display = config.plaqueType === 'typeface' ? 'block' : 'none';
    if (handwrittenEl) handwrittenEl.style.display = config.plaqueType === 'handwritten' ? 'block' : 'none';
  }

  const plaqueMessageEl = document.getElementById('cb-plaque-message');
  if (plaqueMessageEl) plaqueMessageEl.value = config.plaqueMessage || '';

  document.querySelectorAll('.cb-align-opt').forEach(o => {
    o.classList.toggle('active', o.dataset.align === config.plaqueAlign);
  });

  document.querySelectorAll('#cb-plaque-font-grid .cb-font-opt').forEach(o => {
    o.classList.toggle('active', o.dataset.font === config.plaqueFont);
  });

  const notes = document.getElementById('cb-additional-notes');
  if (notes) notes.value = config.additionalNotes || '';

  renderArtworkOptions();
  setArtwork(config.artwork);
  updateFullCustomNotice();
  updatePrice();
  updateRALPaneVisibility();
}

function loadConfigFromURL() {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('config');
  if (!encoded) return false;
  return deserialiseConfig(encoded);
}

// ─── Save modal ───────────────────────────────────────────────────────────────

document.getElementById('cb-save').addEventListener('click', () => {
  openSaveModal();
});

function openSaveModal() {
  const url = getSaveURL();
  document.getElementById('cb-save-url').value = url;
  document.getElementById('cb-save-modal').style.display = 'flex';
}

function closeSaveModal() {
  document.getElementById('cb-save-modal').style.display = 'none';
  document.getElementById('cb-save-email').value = '';
  document.getElementById('cb-save-email-sent').style.display = 'none';
}

document.getElementById('cb-save-modal-close').addEventListener('click', closeSaveModal);

document.getElementById('cb-save-modal').addEventListener('click', (e) => {
  if (e.target === document.getElementById('cb-save-modal')) closeSaveModal();
});

document.getElementById('cb-save-copy').addEventListener('click', () => {
  const url = document.getElementById('cb-save-url').value;
  navigator.clipboard.writeText(url).then(() => {
    const btn = document.getElementById('cb-save-copy');
    btn.textContent = 'Copied!';
    setTimeout(() => btn.textContent = 'Copy link', 2000);
  });
});

// Emailing a build to yourself is the one point where a visitor volunteers an
// email, so it is recorded as an enquiry too. Until an endpoint is connected
// the link is handed to their mail client, which genuinely delivers it.
document.getElementById('cb-save-email-btn').addEventListener('click', async () => {
  const emailEl = document.getElementById('cb-save-email');
  const email = (emailEl ? emailEl.value : '').trim();
  const url = document.getElementById('cb-save-url').value;
  const sent = document.getElementById('cb-save-email-sent');
  const btn = document.getElementById('cb-save-email-btn');
  const err = document.getElementById('cb-save-email-error');
  const consentEl = document.getElementById('cb-save-consent');
  const consent = consentEl ? consentEl.checked : false;

  if (err) err.style.display = 'none';
  if (sent) sent.style.display = 'none';

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
    if (err) { err.textContent = 'Please enter a valid email address.'; err.style.display = 'block'; }
    return;
  }

  const payload = {
    type: ENQUIRY_TYPES.savedBuild,
    submittedAt: new Date().toISOString(),
    contact: { name: '', firstName: '', lastName: '', email: email, phone: '', phoneE164: null },
    consent: { marketing: consent },
    build: buildEnquiryBuild(),
    context: { withinBusinessHours: isBusinessHours(), page: window.location.pathname }
  };

  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  const ok = await submitEnquiry(payload);
  if (btn) { btn.disabled = false; btn.textContent = 'Send'; }

  if (isPrototypeMode() || !ok) {
    // No service to send the email yet, so open their mail client rather than
    // claiming an email is on its way that nothing is going to send.
    const subject = encodeURIComponent('Your Bramwell Brown clock build');
    const body = encodeURIComponent(
      'Here is the link to your saved clock configuration:\n\n' + url +
      '\n\nOpen the link to carry on customising your clock.'
    );
    window.location.href = 'mailto:' + email + '?subject=' + subject + '&body=' + body;
    if (sent) {
      sent.textContent = 'Your email client will open with the link ready to send.';
      sent.style.display = 'block';
    }
  } else if (sent) {
    sent.textContent = 'Sent — check your inbox for the link to your build.';
    sent.style.display = 'block';
  }
});

// ─── Help modal ───────────────────────────────────────────────────────────────

document.getElementById('cb-help-btn').addEventListener('click', () => {
  document.getElementById('cb-help-overlay').style.display = 'flex';
});

document.getElementById('cb-help-close').addEventListener('click', () => {
  document.getElementById('cb-help-overlay').style.display = 'none';
});

document.getElementById('cb-help-overlay').addEventListener('click', (e) => {
  if (e.target === document.getElementById('cb-help-overlay')) {
    document.getElementById('cb-help-overlay').style.display = 'none';
  }
});

// ─── Commission contact controls ─────────────────────────────────────────────

// These appear in both the review panel and the how-it-works modal.
document.querySelectorAll('.cb-commission-chat').forEach(btn => {
  btn.addEventListener('click', async () => {
    // Our overlay sits above the chat panel, so leaving it open hides the very
    // thing the button just opened — and the next click on the activator, which
    // is a toggle, would shut the chat again. Close ours the moment the chat is
    // up. openLiveChat is async, so this must await it: an un-awaited call
    // returns a truthy Promise and the fallback below would never fire.
    if (await openLiveChat()) { closeCommissionModal(); return; }
    // No chat widget on the page, or it hasn't loaded — offer a callback
    // instead of doing nothing.
    openCallbackModal();
  });
});

document.querySelectorAll('.cb-commission-callback').forEach(btn => {
  btn.addEventListener('click', openCallbackModal);
});

// ─── How a commission works ──────────────────────────────────────────────────
// The process detail lives here rather than in the step 1 flow, so neither the
// artwork choice nor the review summary gets buried under it.
function openCommissionModal() {
  const modal = document.getElementById('cb-commission-modal');
  if (!modal) return;
  updateCommissionFigures();
  modal.style.display = 'flex';
}

function closeCommissionModal() {
  const modal = document.getElementById('cb-commission-modal');
  if (modal) modal.style.display = 'none';
}

document.querySelectorAll('.cb-commission-how').forEach(btn => {
  btn.addEventListener('click', openCommissionModal);
});

const commissionCloseEl = document.getElementById('cb-commission-modal-close');
if (commissionCloseEl) commissionCloseEl.addEventListener('click', closeCommissionModal);

const commissionModalEl = document.getElementById('cb-commission-modal');
if (commissionModalEl) {
  commissionModalEl.addEventListener('click', (e) => {
    if (e.target === commissionModalEl) closeCommissionModal();
  });
}

// Escape closes whichever overlay is open, which matters more now that the
// process detail is behind one.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  closeCommissionModal();
  closeCallbackModal();
});

const callbackCloseEl = document.getElementById('cb-callback-modal-close');
if (callbackCloseEl) callbackCloseEl.addEventListener('click', closeCallbackModal);

const callbackModalEl = document.getElementById('cb-callback-modal');
if (callbackModalEl) {
  callbackModalEl.addEventListener('click', (e) => {
    if (e.target === callbackModalEl) closeCallbackModal();
  });
}

// Bound to the form's submit, not the button's click, so the browser's own
// required-field checks run first and the Enter key behaves.
const callbackFormEl = document.getElementById('cb-callback-form');
if (callbackFormEl) {
  callbackFormEl.addEventListener('submit', (e) => { sendCallbackRequest(e); });
}

const callbackCopyEl = document.getElementById('cb-callback-copy');
if (callbackCopyEl) {
  callbackCopyEl.addEventListener('click', () => {
    const input = document.getElementById('cb-callback-link');
    if (!input) return;
    navigator.clipboard.writeText(input.value).then(() => {
      callbackCopyEl.textContent = 'Copied';
      setTimeout(() => { callbackCopyEl.textContent = 'Copy link'; }, 2000);
    });
  });
}

// ─── Diagnostics ─────────────────────────────────────────────────────────────
// Run _diag() in the console to see which parts of the builder are actually
// present on the page, and why something might not be showing.
window._diag = function () {
  const rows = [];
  const check = (label, id) => {
    const el = document.getElementById(id);
    rows.push({
      part: label,
      'in page': !!el,
      display: el ? getComputedStyle(el).display : '—',
      'styled?': el ? (getComputedStyle(el).padding !== '0px' || getComputedStyle(el).display === 'grid') : '—',
      children: el ? el.children.length : '—'
    });
  };

  check('Artwork grid', 'cb-artwork-grid');
  check('How-it-works modal', 'cb-commission-modal');
  check('Callback modal', 'cb-callback-modal');
  check('Pricing block', 'cb-pricing');
  check('Settings block', 'cb-settings');
  check('Price breakdown', 'cb-price-breakdown');
  check('Footer: back', 'cb-nav-back');
  check('Footer: next', 'cb-nav-next');

  console.table(rows);
  console.log('Artwork selected:', config.artwork, '| full custom:', isFullCustom());
  console.log('Full custom notices found:', document.querySelectorAll('.cb-fullcustom-notice').length, '(expect 5)');
  console.log('Artwork cards rendered:', document.querySelectorAll('.cb-artwork-card').length, '(expect 2)');
  console.log('Pricing source:', PRICING.source, '| currency:', CURRENCY.code, '| market:', PRICING.market);
  console.log('Commission price:', formatMoney(PRICES.fullCustom[config.model]));
  console.log('Settings block:', document.getElementById('cb-settings')
    ? 'found' : 'MISSING \u2014 your model URLs and tuned values live there');
  console.log('Artwork GLBs configured:',
    'weather =', MODEL_URLS.weather.artworkGlb || 'not set',
    '| giant =', MODEL_URLS.giant.artworkGlb || 'not set');
  const vids = {
    'model.weather.default': CART.model.weather.default,
    'model.weather.copper': (CART.model.weather.byMaterial || {}).copper,
    'model.weather.aluminium': (CART.model.weather.byMaterial || {}).aluminium,
    'model.giant.default': CART.model.giant.default,
    'addon.custom-ral': CART.addons.material['custom-ral'],
    'addon.faceText.weather': (CART.addons.faceText || {}).weather,
    'addon.faceText.giant': (CART.addons.faceText || {}).giant,
    'addon.plaque.typeface': (CART.addons.plaque || {}).typeface,
    'addon.plaque.handwritten': (CART.addons.plaque || {}).handwritten,
    'addon.commissionDeposit': CART.addons.commissionDeposit,
    'addon.commission': CART.addons.commission
  };
  console.log('Cart variants set:', Object.entries(vids)
    .filter(([, v]) => variantId(v)).map(([k]) => k).join(', ') || '(none)');
  console.log('Cart variants MISSING:', Object.entries(vids)
    .filter(([, v]) => !variantId(v)).map(([k]) => k).join(', ') || '(none)');
  console.log('Commission route:', commissionMode(),
    '| deposit:', depositIsFixed()
      ? formatMoney(depositAmount()) + ' fixed'
      : DEPOSIT_PERCENT + '% of total');
  console.log('Run _cart() to see the line items this build would send.');
  console.log('Run _art() to check the artwork layers.');

  console.log('Step:', currentStep + 1, 'of', STEP_NEXT_LABELS.length,
    '| next button:', (document.getElementById('cb-nav-next') || {}).textContent);

  const blocks = document.querySelectorAll('[data-fullcustom]');
  console.log('Full custom blocks:', blocks.length, '(expect 3 \u2014 brief, terms, contact)');
  if (blocks.length && !isFullCustom()) {
    console.log('%cThey are hidden because standard artwork is selected. Select '
      + '"Custom Artwork" in step 2, or run _showCommission().', 'color:#b8860b');
  }

  // The sticky bar is the one thing that must never be unstyled — without the
  // CSS it scrolls away with the content and the pane nav is gone.
  const footer = document.querySelector('.cb-panel__footer');
  if (footer && getComputedStyle(footer).position === 'static') {
    console.warn('The footer is not sticky, which means clock-builder-artwork.css has not '
      + 'been added to clock-builder.css. Back/Next will scroll out of view.');
  }
  return 'See table above.';
};

// Show the exact line items this build would POST to /cart/add.js, without
// sending anything. The quickest way to check a variant map and to see what the
// workshop will read on the order.
window._cart = function () {
  const { items, missing } = buildCartItems();

  console.table(items.map(it => ({
    variant: it.id,
    qty: it.quantity,
    properties: it.properties ? Object.keys(it.properties).length : 0
  })));

  if (missing.length) {
    console.warn('Missing variant ids for:', missing.join(', '),
      '— the build would be refused rather than part-ordered.');
  }
  if (commissionNeedsEnquiry()) {
    console.log('%cThis is a commission, so the button opens the enquiry form rather '
      + 'than adding to the cart.', 'color:#b8860b');
  }

  const base = items[0];
  if (base && base.properties) {
    console.log('Properties on the base line item:');
    console.table(Object.entries(base.properties).map(([k, v]) => ({
      property: k,
      value: String(v).length > 70 ? String(v).slice(0, 70) + '…' : v,
      'shown to customer': !k.startsWith('_')
    })));
  }

  console.log('Builder total:', formatMoney(calcTotal()),
    '— Shopify will charge the sum of these variants, so the two only agree if '
    + 'each variant price matches #cb-pricing for this market.');
  return { items: items, missing: missing };
};

// Dump what's inside the chat widget's shadow roots. Shopify's chat has no
// documented API, so if the activator selector misses, this shows exactly what
// to target instead.
window._chat = function () {
  const host = document.querySelector('shopify-chat');
  if (!host) {
    console.warn('No <shopify-chat> on the page. Other providers checked via globals:',
      ['ShopifyChat', 'Intercom', 'tidioChatApi', '$crisp', 'GorgiasChat', 'zE']
        .filter(k => window[k]).join(', ') || 'none found');
    return 'No Shopify chat element.';
  }

  console.log('Host attributes:', [...host.attributes].map(a => a.name + '=' + a.value).join(' '));
  console.log('Host has shadowRoot:', !!host.shadowRoot);

  const rows = [];
  const walk = (root, depth, path) => {
    if (!root || depth > 4) return;
    for (const el of root.querySelectorAll('*')) {
      const part = el.getAttribute && el.getAttribute('part');
      const label = el.getAttribute && el.getAttribute('aria-label');
      const interesting = el.tagName === 'BUTTON' || part
        || (el.className && /activator|launcher|bubble|toggle/i.test(String(el.className)));
      if (interesting) {
        rows.push({
          depth: depth,
          tag: el.tagName.toLowerCase(),
          class: String(el.className || '').slice(0, 44),
          part: part || '',
          'aria-label': label || '',
          path: path
        });
      }
      if (el.shadowRoot) walk(el.shadowRoot, depth + 1, path + ' > ' + el.tagName.toLowerCase() + '#shadow');
    }
  };
  walk(host.shadowRoot, 1, 'shopify-chat#shadow');

  if (rows.length) console.table(rows);
  else console.warn('Nothing button-like or part-named found — the roots may be closed.');

  const found = deepQuery(host.shadowRoot,
    '[part~="activator"], button.activator, .activator button, .activator, '
    + 'button[aria-label*="hat"], button[aria-label*="essage"]');
  console.log('Current selector matches:', found
    ? found.tagName.toLowerCase() + ' part=' + (found.getAttribute('part') || '-')
      + ' class=' + (found.className || '-')
    : 'NOTHING — paste the table above and I’ll fix the selector');

  return 'See table above. Run openLiveChat-equivalent by clicking the button in step 7.';
};

// Report which art layers are visible and where each one came from. The layer
// swap is the fiddly part of a custom commission: a layer can come from the
// clock GLB, from the artwork GLB, or be missing entirely.
window._art = function () {
  const rows = [];
  baseArtMeshes.forEach(o => {
    rows.push({ mesh: o.name, source: 'clock GLB', visible: o.visible });
  });
  if (activeArtworkGroup) {
    activeArtworkGroup.traverse(o => {
      if (o.isMesh) rows.push({ mesh: o.name, source: 'artwork GLB', visible: o.visible });
    });
  }
  console.table(rows);

  const expected = ['art_background', 'art_layer_1', 'art_layer_2', 'art_layer_3'];

  const option = (ARTWORK[config.model] || {})[config.artwork] || {};
  console.log('Model:', config.model, '| artwork:', config.artwork);
  console.log('Configured GLB:', option.glb || '(none \u2014 standard artwork)');
  console.log('Cached artwork groups:', [...artworkCache.keys()].join(', ') || '(none)');
  console.log('Artwork group attached:', activeArtworkGroup ? activeArtworkGroup.name : 'no');
  console.log('Supplied by artwork GLB:',
    activeArtworkGroup ? layersSuppliedBy(activeArtworkGroup).join(', ') : '(nothing \u2014 standard artwork)');

  // A multi-material object arrives as several meshes, so count by layer and
  // by which GLB they came from rather than by mesh name.
  const summary = expected.map(layer => {
    const mine = rows.filter(r => isLayerMesh(r.mesh, layer) && r.visible);
    const sources = [...new Set(mine.map(r => r.source))];
    return {
      layer: layer,
      'visible meshes': mine.length,
      source: sources.join(' + ') || '\u2014 NONE \u2014'
    };
  });
  console.table(summary);

  const absent = summary.filter(r => r['visible meshes'] === 0).map(r => r.layer);
  const doubled = summary.filter(r => r.source.includes('+')).map(r => r.layer);

  if (absent.length) {
    console.warn('No visible mesh for:', absent.join(', '),
      '\u2014 the base layer is hidden and the artwork GLB supplies no replacement.');
  }
  if (doubled.length) {
    console.warn('Visible meshes from BOTH GLBs for:', doubled.join(', '),
      '\u2014 the base layer was not hidden, so these will z-fight.');
  }
  if (!absent.length && !doubled.length) {
    console.log('Every layer resolves to exactly one source.');
  }
  return 'See tables above.';
};

// Open the commission content without selecting custom artwork, for checking
// the layout and copy.
window._showCommission = function () {
  document.querySelectorAll('[data-fullcustom]').forEach(el => { el.style.display = ''; });
  document.querySelectorAll('.cb-fullcustom-notice').forEach(el => { el.style.display = 'block'; });
  updateCommissionFigures();
  openCommissionModal();
  return 'Commission blocks shown and modal opened. Reselect artwork to restore.';
};

// ─── Boot ────────────────────────────────────────────────────────────────────

initRAL();
renderStaticPrices();
updateFooterNav();
warnOnDepositMismatch();
updateRALPaneVisibility();
renderArtworkOptions();
applyFaceTextState();
updateFullCustomNotice();
updatePrice();
updateMaterialAvailability();
updateFinishDescriptions();
init();

// Returning from a native form post: Shopify has rendered the success notice
// in place of the form, so show it, and put the parked build back.
const callbackWasPosted = !!document.getElementById('cb-callback-sent');
if (callbackWasPosted) {
  const stashed = takeStashedBuild();
  if (stashed && deserialiseConfig(stashed)) {
    const applyStashed = setInterval(() => {
      if (Object.keys(meshes).length > 0) {
        clearInterval(applyStashed);
        applyDeserialisedConfig();
        updateFaceTexture();
        updatePlaqueTexture();
      }
    }, 100);
  }
  const modal = document.getElementById('cb-callback-modal');
  if (modal) modal.style.display = 'flex';

  // Shopify's success state is driven by ?contact_posted=true in the URL, so it
  // is sticky: reloading that URL re-renders the notice and would reopen this
  // modal every time. Clear the query string now that the confirmation has been
  // shown. Safe to drop the whole thing here — a post-submit URL carries only
  // Shopify's flag, since the redirect has already discarded our ?config=.
  try {
    window.history.replaceState({}, '', window.location.pathname);
  } catch (e) {
    // Not fatal: the worst case is the old sticky behaviour.
    if (DEBUG) console.warn('[clock-builder] Could not clear the posted flag:', e);
  }
}

const hasSavedConfig = loadConfigFromURL();
if (hasSavedConfig) {
  const applyWhenReady = setInterval(() => {
    if (Object.keys(meshes).length > 0) {
      clearInterval(applyWhenReady);
      applyDeserialisedConfig();
      updateFaceTexture();
      updatePlaqueTexture();
    }
  }, 100);
}

// Unconditional: this one line answers "did the script run at all", which is
// the first thing to check when something doesn't appear. Remove before launch
// along with the window._* helpers.
console.log('[clock-builder] Boot complete. Run _diag() to check what is on the page.');